import { useState, useEffect, useLayoutEffect, useRef, useCallback, useMemo, startTransition, Fragment } from 'react'
import { useSearchParams } from 'react-router-dom'
import { TrashIcon, ArchiveBoxIcon, ArrowUturnLeftIcon, XMarkIcon, ArrowUpTrayIcon, ExclamationTriangleIcon, DocumentDuplicateIcon, ArrowPathIcon, QuestionMarkCircleIcon, UserGroupIcon, CheckCircleIcon, SparklesIcon, FunnelIcon, ChevronDownIcon, ChevronUpIcon, PencilSquareIcon, Squares2X2Icon, ArrowsRightLeftIcon } from '@heroicons/react/24/outline'
import {
  getDocuments,
  getDocumentFlagFacets,
  getDocument,
  approveDocument,
  overwriteApprovedDuplicate,
  unapproveDocument,
  deleteDocument,
  permanentlyDeleteDocument,
  restoreDocument,
  refreshDocumentCategories,
  bulkMatchVendors,
  checkQboDuplicates,
  bulkUpdateDocumentMetadata,
  allocateSharedCosts,
  uploadDocument,
  isAmazonReportUploadResponse,
  type BulkMetadataUpdateRequest,
} from '@/services/documents'
import type { CheckQboDuplicatesResult, BulkReprocessResult } from '@/services/documents'
import {
  startBulkApprove,
  startBulkReprocess,
  startBulkUpload,
  startBulkAllocateSharedCosts,
  startBulkRefreshCategoriesJob,
  isFullDocumentJobPayload,
  type BackgroundJob,
} from '@/services/jobs'
import { getEmailRules } from '@/services/emailRules'
import type { EmailRule } from '@/types/emailRule'
import {
  getVendors,
  getLocations,
  getCategories,
  getPaymentTerms,
  createVendor,
} from '@/services/qbo'
import type { QBOVendor, QBOLocation, QBOCategory, QBOTerm } from '@/services/qbo'
import SearchableSelect, { type SelectOption } from '@/components/SearchableSelect'
import type { Document } from '@/types/document'
import DocumentPanel from '@/components/DocumentPanel'
import DateInput from '@/components/DateInput'
import { formatCalendarDate, formatLocalDateTime } from '@/utils/dateFormat'
import { formatApiError, getApiErrorDetail } from '@/utils/apiError'
import SortableHeader from '@/components/SortableHeader'
import { ActiveFilterPillBar, GroupBySelect, TablePageShell } from '@/tableControls'
import {
  DOC_REVIEW_EMPTY_FILTERS,
  generateDocReviewFilterPills,
  removeDocReviewFilterPill,
  type AmountFilterOp,
  type DocReviewFilters,
} from '@/utils/docReviewFilterPills'
import { useBackgroundJobsStore } from '@/store/backgroundJobsStore'
import { toast } from '@/store/toastStore'
import {
  skippedDocQueueTab,
  skippedDuplicateToastActionLabel,
  skippedDuplicateToastMessage,
} from '@/utils/bulkUploadSkippedToast'
import { useDebounce } from '@/hooks/useDebounce'
import { usePageAccess } from '@/hooks/usePageAccess'
import { useDocumentStore } from '@/store/documentStore'
import { usePendingReviewStore } from '@/store/pendingReviewStore'
import { isDocVendorKnownInQbo } from '@/utils/qboVendorMatch'
import { qboTxnUrl } from '@/utils/qboLinks'
import { docTypeLabel, docTypeRequiresLineItems } from '@/utils/docTypeLabel'
import {
  DOC_REVIEW_GROUP_OPTIONS,
  buildDocReviewGroups,
  buildPendingPeerDuplicateIds,
  docReviewCountDocSuffix,
  docReviewCountFilteredSuffix,
  docReviewCountOfTotal,
  docReviewPageRange,
  nextDocReviewGroupBy,
  uniqueDocsInDisplayOrder,
  type DocReviewGroupBy,
} from '@/utils/docReviewGrouping'
import {
  formatAmberReviewFlagLine,
  getAmberReviewFlagsFromNeedsReviewFlags,
  isBulkWarnOnlyReviewFlag,
  isTotalMismatchReviewFlag,
} from '@/utils/docReviewAmberFlags'
import { getSourceDisplay } from '@/utils/docReviewSourceDisplay'

type StatusTab = 'pending_review' | 'approved' | 'deleted'

type Filters = DocReviewFilters

const AMOUNT_FILTER_OP_VALUES: AmountFilterOp[] = ['eq', 'gt', 'gte', 'lt', 'lte']

const AMOUNT_FILTER_OPS: { value: AmountFilterOp; label: string }[] = [
  { value: 'eq', label: '=' },
  { value: 'gt', label: '>' },
  { value: 'gte', label: '≥' },
  { value: 'lt', label: '<' },
  { value: 'lte', label: '≤' },
]

function amountFilterApiParams(
  amount: string,
  amountOp: string,
): { amount_op?: AmountFilterOp; amount?: string } {
  const raw = amount.trim()
  if (!raw || !amountOp) return {}
  const n = parseFloat(raw.replace(/,/g, '').replace(/\$/g, ''))
  if (!Number.isFinite(n)) return {}
  if (!AMOUNT_FILTER_OP_VALUES.includes(amountOp as AmountFilterOp)) return {}
  return { amount_op: amountOp as AmountFilterOp, amount: String(n) }
}

const emptyFilters: Filters = DOC_REVIEW_EMPTY_FILTERS

const SOURCE_FILTER_OPTIONS: { value: Document['source_type']; label: string }[] = [
  { value: 'email', label: 'Email' },
  { value: 'upload', label: 'Upload' },
  { value: 'drive_scan', label: 'Scan' },
  { value: 'amazon_report', label: 'Amazon' },
]

const LIST_PAGE_SIZE = 50
const GROUP_FETCH_PAGE_SIZE = 100

const FLAG_OPTIONS = [
  { value: 'duplicate', label: 'Duplicate in pending' },
  { value: 'duplicate_approved', label: 'Duplicate in approved' },
  { value: 'qbo_duplicate', label: 'Duplicate in QBO' },
  { value: 'missing_categories', label: 'Missing categories' },
  { value: 'missing_doc_type', label: 'Missing doc type' },
  { value: 'missing_location', label: 'Missing location' },
  { value: 'missing_unknown_vendor', label: 'Missing/unknown vendor' },
  { value: 'missing_bill_dates', label: 'Missing bill issue or due date' },
  { value: 'missing_line_items', label: 'No line items (bill or vendor credit)' },
  { value: 'non_payable_notice', label: 'Not an invoice / do not pay' },
  { value: 'total_mismatch', label: 'Total mismatch' },
  { value: 'shared_costs', label: 'Shared costs (tax, shipping, discount, tip, fees, …)' },
  { value: 'credit_negative_lines', label: 'Vendor credit mixed-sign lines' },
  { value: 'new_reader_fallback', label: 'New reader fallback to legacy' },
  { value: 'amazon_charge_incomplete', label: 'Amazon: charge not fully reported yet' },
  { value: 'amazon_report_consistency', label: 'Amazon: report rows disagreed' },
] as const

const EMAIL_RULE_HINT_LABEL_NOTES_MAX = 72

/** Label for email-rule rows in parsing-hints dropdowns: `Vendor (notes)`. */
function emailRuleHintOptionLabel(rule: EmailRule): string {
  const raw = (rule.notes ?? '').trim().replace(/\s+/g, ' ')
  const inner =
    raw.length === 0
      ? 'no notes'
      : raw.length > EMAIL_RULE_HINT_LABEL_NOTES_MAX
        ? `${raw.slice(0, EMAIL_RULE_HINT_LABEL_NOTES_MAX - 1)}…`
        : raw
  return `${rule.vendor_name} (${inner})`
}

function partitionOpenDocumentFromBulkIds(
  documentIds: number[],
  openDocumentId: number | null | undefined,
): { jobDocumentIds: number[]; syncOpenDocumentId: number | null } {
  if (openDocumentId == null || !documentIds.includes(openDocumentId)) {
    return { jobDocumentIds: documentIds, syncOpenDocumentId: null }
  }
  return {
    jobDocumentIds: documentIds.filter((id) => id !== openDocumentId),
    syncOpenDocumentId: openDocumentId,
  }
}

export default function DocReview() {
  const { canEdit } = usePageAccess('invoices')
  const [documents, setDocuments] = useState<Document[]>([])
  const [loading, setLoading] = useState(true)
  const [activeTab, setActiveTab] = useState<StatusTab>('pending_review')
  const [selectedDocument, setSelectedDocument] = useState<Document | null>(null)
  /** List rows omit heavy fields; we refetch full document for the side panel preview. */
  const [documentPreviewHydrating, setDocumentPreviewHydrating] = useState(false)
  const documentHydrateGenRef = useRef(0)
  const [searchParams, setSearchParams] = useSearchParams()
  const [total, setTotal] = useState(0)
  /**
   * Flag keys present on at least one row in the current table cohort (all pages, same
   * column filters as the list, excluding the Flags filter). Null until the first fetch completes.
   */
  const [flagFacets, setFlagFacets] = useState<string[] | null>(null)
  const [listPage, setListPage] = useState(1)
  const [listSortKey, setListSortKey] = useState<string>('created_at')
  const [listSortDir, setListSortDir] = useState<'asc' | 'desc'>('desc')
  const [qboLocationNames, setQboLocationNames] = useState<string[]>([])
  const [uploading, setUploading] = useState(false)
  const [filters, setFilters] = useState<Filters>(emptyFilters)
  const [selectedIds, setSelectedIds] = useState<Set<number>>(new Set())
  /** True after user chooses "Select all … in this view" (all pages of current filters). */
  const [fullCohortSelected, setFullCohortSelected] = useState(false)
  const [isSelectingAllInView, setIsSelectingAllInView] = useState(false)
  const [isApproving, setIsApproving] = useState(false)
  const [approvalError, setApprovalError] = useState<string | null>(null)
  const [isOverwriting, setIsOverwriting] = useState(false)
  const [overwriteError, setOverwriteError] = useState<string | null>(null)
  const [isDraggingOver, setIsDraggingOver] = useState(false)
  const [isStartingRefreshCategories, setIsStartingRefreshCategories] = useState(false)
  const [isMatchingVendors, setIsMatchingVendors] = useState(false)
  const [qboActiveVendors, setQboActiveVendors] = useState<QBOVendor[]>([])
  const [isBulkApproving, setIsBulkApproving] = useState(false)
  const [isCheckingDuplicates, setIsCheckingDuplicates] = useState(false)
  const [duplicateCheckResult, setDuplicateCheckResult] = useState<CheckQboDuplicatesResult | null>(null)
  const [isReprocessing, setIsReprocessing] = useState(false)
  const [reprocessResult, setReprocessResult] = useState<BulkReprocessResult | null>(null)
  /** Modal for choosing optional extraction hints (email rule) before upload or bulk re-run extraction. */
  const [processingHintsModal, setProcessingHintsModal] = useState<
    null | { kind: 'upload'; files: File[] } | { kind: 'reprocess' }
  >(null)
  /** Rule id string for SearchableSelect inside the hints modal; empty = Auto. */
  const [processingHintsRuleId, setProcessingHintsRuleId] = useState('')
  const [emailRules, setEmailRules] = useState<EmailRule[]>([])
  const [emailRulesLoading, setEmailRulesLoading] = useState(false)
  const [bulkMetadataOpen, setBulkMetadataOpen] = useState(false)
  const [bulkMetaQboLoading, setBulkMetaQboLoading] = useState(false)
  const [bulkMetaSaving, setBulkMetaSaving] = useState(false)
  const [isStartingBulkAllocate, setIsStartingBulkAllocate] = useState(false)
  const [bulkMetaVendors, setBulkMetaVendors] = useState<QBOVendor[]>([])
  const [bulkMetaLocations, setBulkMetaLocations] = useState<QBOLocation[]>([])
  const [bulkMetaCategories, setBulkMetaCategories] = useState<QBOCategory[]>([])
  const [bulkMetaApplyVendor, setBulkMetaApplyVendor] = useState(false)
  const [bulkMetaVendorName, setBulkMetaVendorName] = useState<string | null>(null)
  const [bulkMetaApplyDocType, setBulkMetaApplyDocType] = useState(false)
  const [bulkMetaDocType, setBulkMetaDocType] = useState<'bill' | 'vendor_credit' | ''>('')
  const [bulkMetaApplyLocation, setBulkMetaApplyLocation] = useState(false)
  const [bulkMetaLocationId, setBulkMetaLocationId] = useState('')
  const [bulkMetaApplyMemo, setBulkMetaApplyMemo] = useState(false)
  const [bulkMetaMemo, setBulkMetaMemo] = useState('')
  const [bulkMetaApplyLineCategory, setBulkMetaApplyLineCategory] = useState(false)
  const [bulkMetaCategoryId, setBulkMetaCategoryId] = useState<string | null>(null)
  const [bulkMetaTerms, setBulkMetaTerms] = useState<QBOTerm[]>([])
  const [bulkMetaApplyTerms, setBulkMetaApplyTerms] = useState(false)
  const [bulkMetaTermId, setBulkMetaTermId] = useState('')
  const bulkMetadataModalBusy = bulkMetaSaving
  const processingHintsModalBusy =
    processingHintsModal == null
      ? false
      : processingHintsModal.kind === 'upload'
        ? uploading
        : isReprocessing
  const [showFilters, setShowFilters] = useState(false)
  const [groupBy, setGroupBy] = useState<DocReviewGroupBy>('none')
  const [collapsedGroupKeys, setCollapsedGroupKeys] = useState<Set<string>>(() => new Set())
  /** Query key for which `documents` already holds the full filtered cohort (grouped view). */
  const fullCohortQueryKeyRef = useRef<string | null>(null)
  const inFlightCohortKeyRef = useRef<string | null>(null)
  /** Unfiltered tab total for approved/archived when column filters are active. */
  const [tabUnfilteredTotal, setTabUnfilteredTotal] = useState<number | null>(null)
  const pendingReviewUnfilteredTotal = usePendingReviewStore((s) => s.count)
  const fileInputRef = useRef<HTMLInputElement>(null)
  const dropZoneRef = useRef<HTMLDivElement>(null)
  const dragCounterRef = useRef(0)
  const bulkUploadMergedDocIdsRef = useRef<Set<number>>(new Set())
  const bulkUploadSkippedToastShownRef = useRef<Set<number>>(new Set())
  const amazonReportBulkToastShownRef = useRef<Set<number>>(new Set())
  const handledBulkReprocessCompletionRef = useRef<Set<number>>(new Set())
  const bulkJobMergedDocCountRef = useRef<Record<number, number>>({})
  const selectedDocumentIdRef = useRef<number | null>(null)
  selectedDocumentIdRef.current = selectedDocument?.id ?? null

  const registerServerJob = useBackgroundJobsStore((s) => s.registerServerJob)
  const startSynthetic = useBackgroundJobsStore((s) => s.startSynthetic)
  const completeSynthetic = useBackgroundJobsStore((s) => s.completeSynthetic)
  const failSynthetic = useBackgroundJobsStore((s) => s.failSynthetic)

  /**
   * Only bulk-upload job ids — stable while unrelated jobs (e.g. bulk_email_process) poll.
   * Avoids re-rendering all of DocReview (and DocumentPanel) on every email-ingestion tick.
   */
  const bulkUploadIdsKey = useBackgroundJobsStore((s) =>
    Object.values(s.serverById)
      .filter((r) => r.job.job_type === 'bulk_upload')
      .map((r) => r.job.id)
      .sort((a, b) => a - b)
      .join(',')
  )

  /**
   * Fingerprint of bulk-upload state needed to merge new docs into the table.
   * Email/QBO jobs updating the global store do not change this string.
   */
  const bulkUploadMergeKey = useBackgroundJobsStore((s) =>
    Object.values(s.serverById)
      .filter((r) => r.job.job_type === 'bulk_upload')
      .map((r) => {
        const j = r.job
        const ids = j.result_data?.uploaded_docs?.map((u) => u.id).join(',') ?? ''
        const skipped = j.result_data?.skipped_docs?.length ?? 0
        return `${j.id}:${j.status}:${j.processed_items}:${ids}:${skipped}`
      })
      .sort()
      .join('|')
  )

  const bulkReprocessJobsKey = useBackgroundJobsStore((s) =>
    Object.values(s.serverById)
      .filter((r) => r.job.job_type === 'bulk_reprocess')
      .map(
        (r) =>
          `${r.job.id}:${r.job.status}:${r.job.processed_items}:${r.job.successful_items}:${r.job.failed_items}:${r.job.error_message ?? ''}:${r.job.result_data?.reprocessed_docs?.length ?? 0}`
      )
      .sort()
      .join('|')
  )

  const bulkAllocateMergeKey = useBackgroundJobsStore((s) =>
    Object.values(s.serverById)
      .filter((r) => r.job.job_type === 'bulk_allocate_shared_costs')
      .map((r) => {
        const j = r.job
        const n = j.result_data?.updated_docs?.length ?? 0
        return `${j.id}:${j.status}:${j.processed_items}:${n}`
      })
      .sort()
      .join('|')
  )

  const bulkRefreshCategoriesMergeKey = useBackgroundJobsStore((s) =>
    Object.values(s.serverById)
      .filter((r) => r.job.job_type === 'bulk_refresh_categories')
      .map((r) => {
        const j = r.job
        const n = j.result_data?.updated_docs?.length ?? 0
        return `${j.id}:${j.status}:${j.processed_items}:${n}`
      })
      .sort()
      .join('|')
  )

  const bulkMetaVendorOptions: SelectOption[] = useMemo(
    () =>
      bulkMetaVendors.map((v) => ({
        id: v.display_name,
        label: v.display_name,
      })),
    [bulkMetaVendors]
  )

  const bulkMetaCategoryOptions: SelectOption[] = useMemo(
    () =>
      bulkMetaCategories
        .filter((c) => c.active)
        .map((c) => ({
          id: c.id,
          label: c.name,
          group: c.account_type || 'Other',
        })),
    [bulkMetaCategories]
  )

  const processingHintsRuleOptions: SelectOption[] = useMemo(() => {
    const fromRules = emailRules.map((r) => ({
      id: String(r.id),
      label: emailRuleHintOptionLabel(r),
      group:
        r.status === 'confirmed' ? 'Email rules' : r.status === 'draft' ? 'Draft rules' : 'Disabled rules',
    }))
    return [{ id: '', label: 'Auto' }, ...fromRules]
  }, [emailRules])

  /** Row index in `listDocuments` for the next SHIFT+click range; each range unions into `selectedIds` */
  const selectionAnchorIndexRef = useRef<number | null>(null)
  /**
   * After shift+range selection (mousedown preventDefault), React still delivers `change`
   * with checked=false for the endpoint row. Hold this ref until that event is ignored.
   * Do not clear it in a microtask — that runs before `change` and breaks suppression.
   */
  const suppressCheckboxChangeForDocIdRef = useRef<number | null>(null)
  /** Documents loaded when selecting all rows across pages (for bulk actions that need row data). */
  const cohortExtraDocsRef = useRef<Map<number, Document>>(new Map())
  const selectAllCohortSeqRef = useRef(0)
  const headerSelectAllCheckboxRef = useRef<HTMLInputElement>(null)

  const debouncedVendor = useDebounce(filters.vendor, 300)
  const debouncedAmount = useDebounce(filters.amount, 300)

  const apiAmountParams = useMemo(
    () => amountFilterApiParams(debouncedAmount, filters.amount_op),
    [debouncedAmount, filters.amount_op],
  )

  const activeTabRef = useRef<StatusTab>(activeTab)
  activeTabRef.current = activeTab
  const groupByRef = useRef<DocReviewGroupBy>(groupBy)
  groupByRef.current = groupBy
  const listPageRef = useRef(listPage)
  listPageRef.current = listPage
  const docLoadSeqRef = useRef(0)
  /** Bumped when the facet cohort changes or a new fetch starts; stale /flag-facets responses are ignored. */
  const flagFacetSeqRef = useRef(0)
  /** Latest list rows for comparing before merge (e.g. duplicate-cohort refresh scheduling). */
  const documentsRef = useRef<Document[]>([])
  documentsRef.current = documents
  const totalRef = useRef(0)
  totalRef.current = total
  /** Debounced GET /documents without loading UI — recomputes duplicate_* and flags vs full DB after local cohort edits. */
  const silentCohortRefreshTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const loadDocumentsRef = useRef<
    (opts?: { silent?: boolean; skipIfCohortReady?: boolean }) => Promise<void>
  >(async () => {})

  const apiFlagsForRequest = useMemo(
    () => filters.flags || undefined,
    [filters.flags],
  )

  const getListQueryParams = useCallback(() => {
    return {
      status: activeTab,
      vendor: debouncedVendor || undefined,
      doc_type: filters.doc_type || undefined,
      location: filters.location || undefined,
      source_type: filters.source_type || undefined,
      reference_number: filters.reference_number || undefined,
      date_from: filters.date_from || undefined,
      date_to: filters.date_to || undefined,
      ...apiAmountParams,
      flags: apiFlagsForRequest,
      sort_key: listSortKey,
      sort_dir: listSortDir,
    }
  }, [
    activeTab,
    apiAmountParams,
    apiFlagsForRequest,
    debouncedVendor,
    filters.date_from,
    filters.date_to,
    filters.doc_type,
    filters.location,
    filters.reference_number,
    filters.source_type,
    listSortDir,
    listSortKey,
  ])

  const listDocuments = documents

  function getSelectedDocuments(): Document[] {
    return Array.from(selectedIds)
      .map(
        (id) =>
          listDocuments.find((d) => d.id === id) ?? cohortExtraDocsRef.current.get(id),
      )
      .filter((d): d is Document => d != null)
  }

  const loadDocuments = useCallback(async (opts?: {
    silent?: boolean
    skipIfCohortReady?: boolean
  }) => {
    const silent = opts?.silent ?? false
    const base = getListQueryParams()
    const queryKey = JSON.stringify(base)
    const wantFullCohort = groupByRef.current !== 'none'
    if (opts?.skipIfCohortReady && wantFullCohort) {
      const haveFull =
        totalRef.current > 0 && documentsRef.current.length >= totalRef.current
      if (
        haveFull &&
        (fullCohortQueryKeyRef.current === queryKey ||
          fullCohortQueryKeyRef.current == null)
      ) {
        fullCohortQueryKeyRef.current = queryKey
        return
      }
      if (inFlightCohortKeyRef.current === queryKey) {
        return
      }
    }
    const seq = ++docLoadSeqRef.current
    const requestedTab = activeTab
    try {
      if (!silent) {
        setLoading(true)
      }
      if (wantFullCohort) {
        inFlightCohortKeyRef.current = queryKey
        const first = await getDocuments({
          ...base,
          page: 1,
          page_size: GROUP_FETCH_PAGE_SIZE,
        })
        const stale =
          requestedTab !== activeTabRef.current || seq !== docLoadSeqRef.current
        if (stale) {
          return
        }

        const allDocs = [...first.documents]
        const totalCount = first.total
        const totalPages = Math.max(1, Math.ceil(totalCount / GROUP_FETCH_PAGE_SIZE))
        for (let p = 2; p <= totalPages; p++) {
          const data = await getDocuments({
            ...base,
            page: p,
            page_size: GROUP_FETCH_PAGE_SIZE,
          })
          if (requestedTab !== activeTabRef.current || seq !== docLoadSeqRef.current) {
            return
          }
          allDocs.push(...data.documents)
        }

        setDocuments(allDocs)
        setTotal(totalCount)
        fullCohortQueryKeyRef.current = queryKey
      } else {
        const data = await getDocuments({
          ...base,
          page: listPage,
          page_size: LIST_PAGE_SIZE,
        })
        const stale =
          requestedTab !== activeTabRef.current || seq !== docLoadSeqRef.current
        if (stale) {
          return
        }
        setDocuments(data.documents)
        setTotal(data.total)
        fullCohortQueryKeyRef.current = null
      }
    } catch (err) {
      if (seq !== docLoadSeqRef.current) {
        return
      }
      if (silent) {
        console.error(err)
      } else {
        toast.error('Failed to load documents')
        console.error(err)
      }
    } finally {
      if (seq === docLoadSeqRef.current && inFlightCohortKeyRef.current === queryKey) {
        inFlightCohortKeyRef.current = null
      }
      if (!silent && seq === docLoadSeqRef.current) {
        setLoading(false)
      }
    }
  }, [activeTab, getListQueryParams, listPage])

  loadDocumentsRef.current = loadDocuments

  /** One debounced refetch for the current Review filters/page; skips loading spinner (cheap full-DB duplicate recompute on server). */
  const scheduleSilentPendingCohortRefresh = useCallback(() => {
    if (activeTabRef.current !== 'pending_review') {
      return
    }
    if (silentCohortRefreshTimerRef.current) {
      clearTimeout(silentCohortRefreshTimerRef.current)
    }
    silentCohortRefreshTimerRef.current = setTimeout(() => {
      silentCohortRefreshTimerRef.current = null
      if (activeTabRef.current !== 'pending_review') {
        return
      }
      void loadDocumentsRef.current({ silent: true })
    }, 200)
  }, [])

  useEffect(
    () => () => {
      if (silentCohortRefreshTimerRef.current) {
        clearTimeout(silentCohortRefreshTimerRef.current)
      }
    },
    [],
  )

  useEffect(() => {
    loadDocuments()
  }, [loadDocuments])

  const groupByEffectReadyRef = useRef(false)
  useEffect(() => {
    if (!groupByEffectReadyRef.current) {
      groupByEffectReadyRef.current = true
      return
    }
    if (groupBy === 'none') {
      const rows = documentsRef.current
      if (rows.length > LIST_PAGE_SIZE) {
        const start = (listPageRef.current - 1) * LIST_PAGE_SIZE
        setDocuments(rows.slice(start, start + LIST_PAGE_SIZE))
      }
      fullCohortQueryKeyRef.current = null
      return
    }
    void loadDocumentsRef.current({ silent: true, skipIfCohortReady: true })
  }, [groupBy])

  useEffect(() => {
    function handleGroupByShortcut(e: KeyboardEvent) {
      if (e.key !== 'g' && e.key !== 'G') return
      if (e.ctrlKey || e.metaKey || e.altKey) return
      const target = e.target
      if (!(target instanceof HTMLElement)) return
      const tag = target.tagName
      if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT' || target.isContentEditable) {
        return
      }
      e.preventDefault()
      setGroupBy((current) => nextDocReviewGroupBy(current))
    }
    document.addEventListener('keydown', handleGroupByShortcut)
    return () => document.removeEventListener('keydown', handleGroupByShortcut)
  }, [])

  useEffect(() => {
    if (!processingHintsModal) return
    setProcessingHintsRuleId('')
    let cancelled = false
    ;(async () => {
      try {
        setEmailRulesLoading(true)
        const data = await getEmailRules()
        if (!cancelled) {
          setEmailRules(data.rules)
        }
      } catch (err) {
        console.error(err)
        if (!cancelled) {
          setEmailRules([])
        }
      } finally {
        if (!cancelled) {
          setEmailRulesLoading(false)
        }
      }
    })()
    return () => {
      cancelled = true
    }
  }, [processingHintsModal])

  const loadFlagFacets = useCallback(async () => {
    const seq = flagFacetSeqRef.current
    try {
      const data = await getDocumentFlagFacets({
        status: activeTab,
        vendor: debouncedVendor || undefined,
        doc_type: filters.doc_type || undefined,
        location: filters.location || undefined,
        source_type: filters.source_type || undefined,
        reference_number: filters.reference_number || undefined,
        date_from: filters.date_from || undefined,
        date_to: filters.date_to || undefined,
        ...apiAmountParams,
      })
      if (seq !== flagFacetSeqRef.current) {
        return
      }
      setFlagFacets(data.facets)
    } catch (err) {
      console.error(err)
      if (seq !== flagFacetSeqRef.current) {
        return
      }
      setFlagFacets([])
    }
  }, [
    activeTab,
    apiAmountParams,
    bulkUploadMergeKey,
    debouncedVendor,
    filters.date_from,
    filters.date_to,
    filters.doc_type,
    filters.location,
    filters.reference_number,
    filters.source_type,
  ])

  useEffect(() => {
    setFilters((prev) => (prev.flags ? { ...prev, flags: '' } : prev))
  }, [activeTab])

  /** Same cohort keys as /flag-facets: drop stale options immediately and invalidate in-flight fetches. */
  useEffect(() => {
    flagFacetSeqRef.current += 1
    setFlagFacets(null)
  }, [
    activeTab,
    apiAmountParams,
    bulkUploadMergeKey,
    debouncedVendor,
    filters.date_from,
    filters.date_to,
    filters.doc_type,
    filters.location,
    filters.reference_number,
    filters.source_type,
  ])

  useEffect(() => {
    void loadFlagFacets()
  }, [loadFlagFacets])

  /** New cohort (tab or column filters): selection only applies to one filtered view. */
  useEffect(() => {
    selectAllCohortSeqRef.current += 1
    setIsSelectingAllInView(false)
    setSelectedIds(new Set())
    setFullCohortSelected(false)
    cohortExtraDocsRef.current.clear()
    selectionAnchorIndexRef.current = null
  }, [
    activeTab,
    apiAmountParams,
    apiFlagsForRequest,
    debouncedVendor,
    filters.date_from,
    filters.date_to,
    filters.doc_type,
    filters.location,
    filters.reference_number,
    filters.source_type,
  ])

  /** Drop a Flags value that no longer exists in the cohort after column filters / tab change. */
  useEffect(() => {
    if (flagFacets === null) return
    const current = filters.flags
    if (!current) return
    if (!flagFacets.includes(current)) {
      setListPage(1)
      setFilters((prev) => (prev.flags === current ? { ...prev, flags: '' } : prev))
    }
  }, [flagFacets, filters.flags])

  useLayoutEffect(() => {
    setListPage(1)
  }, [debouncedVendor, debouncedAmount, filters.amount_op])

  useEffect(() => {
    void getLocations()
      .then((locs) =>
        setQboLocationNames(
          locs.filter((l) => l.active).map((l) => l.name).filter(Boolean),
        ),
      )
      .catch(() => {})
  }, [])

  useEffect(() => {
    const raw = searchParams.get('doc')
    if (!raw) return
    const id = Number.parseInt(raw, 10)
    if (Number.isNaN(id)) return

    let cancelled = false
    void (async () => {
      try {
        setDocumentPreviewHydrating(true)
        const doc = await getDocument(id)
        if (cancelled) {
          setDocumentPreviewHydrating(false)
          return
        }
        setListPage(1)
        if (doc.deleted_at) {
          setActiveTab('deleted')
        } else if (doc.status === 'approved') {
          setActiveTab('approved')
        } else {
          setActiveTab('pending_review')
        }
        setSelectedDocument(doc)
        setDocumentPreviewHydrating(false)
        setSearchParams(
          (prev) => {
            const next = new URLSearchParams(prev)
            next.delete('doc')
            return next
          },
          { replace: true },
        )
      } catch {
        if (!cancelled) {
          setDocumentPreviewHydrating(false)
          toast.error(`Could not open document #${raw}`)
        }
      }
    })()
    return () => {
      cancelled = true
    }
  }, [searchParams, setSearchParams])

  const refreshTrigger = useDocumentStore((state) => state.refreshTrigger)

  useEffect(() => {
    if (refreshTrigger > 0) {
      loadDocuments()
    }
  }, [refreshTrigger, loadDocuments])

  const hydrateDocumentFromListRow = useCallback((listDoc: Document) => {
    const gen = ++documentHydrateGenRef.current
    setSelectedDocument(listDoc)
    setDocumentPreviewHydrating(true)
    void getDocument(listDoc.id)
      .then((full) => {
        if (documentHydrateGenRef.current !== gen) return
        setSelectedDocument(full)
        setDocumentPreviewHydrating(false)
      })
      .catch((err) => {
        if (documentHydrateGenRef.current !== gen) return
        console.error(err)
        setDocumentPreviewHydrating(false)
      })
  }, [])

  const refreshSelectedDocumentFromServer = useCallback((docId: number) => {
    const gen = ++documentHydrateGenRef.current
    // Do not toggle documentPreviewHydrating here: that flag is reserved for list→full
    // panel open (DocumentPanel syncs line_items when it clears). Background refetch
    // must not fire the same effect or it can overwrite in-progress line edits.
    void getDocument(docId)
      .then((full) => {
        if (documentHydrateGenRef.current !== gen) return
        setSelectedDocument(full)
      })
      .catch(() => {
        if (documentHydrateGenRef.current !== gen) return
      })
  }, [])

  const mergeDocumentsFromBackgroundJob = useCallback(
    (job: BackgroundJob, resultField: 'reprocessed_docs' | 'updated_docs') => {
      const raw = job.result_data?.[resultField] ?? []
      const mergedCount = bulkJobMergedDocCountRef.current[job.id] ?? 0
      if (raw.length <= mergedCount) return

      const newPayloads = raw.slice(mergedCount).filter(isFullDocumentJobPayload)
      bulkJobMergedDocCountRef.current[job.id] = raw.length
      if (newPayloads.length === 0) return

      const openId = selectedDocumentIdRef.current
      const openInBatch = openId != null && newPayloads.some((d) => d.id === openId)
      if (openInBatch && openId != null) {
        refreshSelectedDocumentFromServer(openId)
      }

      const rowsToMerge = openInBatch
        ? newPayloads.filter((d) => d.id !== openId)
        : newPayloads
      if (rowsToMerge.length === 0) {
        scheduleSilentPendingCohortRefresh()
        return
      }

      startTransition(() => {
        setDocuments((prev) => {
          const m = new Map(rowsToMerge.map((d) => [d.id, d]))
          return prev.map((row) => m.get(row.id) ?? row)
        })
        for (const d of rowsToMerge) {
          if (cohortExtraDocsRef.current.has(d.id)) {
            cohortExtraDocsRef.current.set(d.id, d)
          }
        }
      })
      scheduleSilentPendingCohortRefresh()
    },
    [scheduleSilentPendingCohortRefresh, refreshSelectedDocumentFromServer],
  )

  /** Invalidate in-flight GET /documents/:id (avoids reopening the panel after close). */
  function bumpDocumentHydrationGen() {
    documentHydrateGenRef.current += 1
    setDocumentPreviewHydrating(false)
  }

  useEffect(() => {
    if (!selectedDocument) return
    const fresh = documents.find((d) => d.id === selectedDocument.id)
    if (!fresh) return
    if (fresh.updated_at !== selectedDocument.updated_at) {
      refreshSelectedDocumentFromServer(selectedDocument.id)
    }
  }, [documents, selectedDocument, refreshSelectedDocumentFromServer])

  /** After a silent list refetch, merge cohort-derived fields from the row into the open panel (list omits heavy fields; keep panel body, refresh flags/duplicates). */
  useEffect(() => {
    if (!selectedDocument) return
    const row = documents.find((d) => d.id === selectedDocument.id)
    if (!row) return
    const dupPeer = row.duplicate_peer_pending !== selectedDocument.duplicate_peer_pending
    const dupIn = row.duplicate_in_pending !== selectedDocument.duplicate_in_pending
    const dupAppr = row.duplicate_matches_approved !== selectedDocument.duplicate_matches_approved
    const dupApprDocId =
      row.duplicate_approved_document_id !== selectedDocument.duplicate_approved_document_id
    const dupApprQboId =
      row.duplicate_approved_qbo_entity_id !== selectedDocument.duplicate_approved_qbo_entity_id
    const dupApprQboType =
      row.duplicate_approved_qbo_entity_type !== selectedDocument.duplicate_approved_qbo_entity_type
    const dupApprLineCount =
      row.duplicate_approved_line_item_count !== selectedDocument.duplicate_approved_line_item_count
    const dupApprTotal =
      row.duplicate_approved_total_amount !== selectedDocument.duplicate_approved_total_amount
    const flags =
      JSON.stringify(row.needs_review_flags ?? []) !==
      JSON.stringify(selectedDocument.needs_review_flags ?? [])
    const shared = row.has_unallocated_shared_costs !== selectedDocument.has_unallocated_shared_costs
    if (
      !dupPeer &&
      !dupIn &&
      !dupAppr &&
      !dupApprDocId &&
      !dupApprQboId &&
      !dupApprQboType &&
      !dupApprLineCount &&
      !dupApprTotal &&
      !flags &&
      !shared
    )
      return
    setSelectedDocument((sd) => {
      if (!sd || sd.id !== row.id) return sd
      return {
        ...sd,
        duplicate_peer_pending: row.duplicate_peer_pending,
        duplicate_in_pending: row.duplicate_in_pending,
        duplicate_matches_approved: row.duplicate_matches_approved,
        duplicate_approved_document_id: row.duplicate_approved_document_id,
        duplicate_approved_qbo_entity_id: row.duplicate_approved_qbo_entity_id,
        duplicate_approved_qbo_entity_type: row.duplicate_approved_qbo_entity_type,
        duplicate_approved_line_item_count: row.duplicate_approved_line_item_count,
        duplicate_approved_total_amount: row.duplicate_approved_total_amount,
        needs_review_flags: row.needs_review_flags,
        has_unallocated_shared_costs: row.has_unallocated_shared_costs,
      }
    })
  }, [documents, selectedDocument])

  useEffect(() => {
    const serverJobs = Object.values(useBackgroundJobsStore.getState().serverById)
    for (const { job } of serverJobs) {
      if (job.job_type !== 'bulk_reprocess') continue
      if (job.status !== 'completed' && job.status !== 'failed') continue
      if (handledBulkReprocessCompletionRef.current.has(job.id)) continue
      handledBulkReprocessCompletionRef.current.add(job.id)

      const failed = (job.result_data?.failed_docs ?? []) as BulkReprocessResult['failed_docs']
      const ok = job.successful_items

      if (failed.length > 0) {
        const docs = (job.result_data?.reprocessed_docs ?? []) as Document[]
        setReprocessResult({
          success: true,
          reprocessed_count: ok,
          failed_count: job.failed_items,
          reprocessed_docs: docs,
          failed_docs: failed,
        })
      } else if (ok > 0 && job.status === 'completed') {
        toast.success(`Re-ran extraction on ${ok} document(s).`)
      }
    }
  }, [bulkReprocessJobsKey])

  useEffect(() => {
    async function loadVendors() {
      try {
        const vendors = await getVendors()
        setQboActiveVendors(vendors.filter((v) => v.active))
      } catch (err) {
        console.error('Failed to load QBO vendors:', err)
      }
    }
    loadVendors()
  }, [])

  useEffect(() => {
    if (!bulkMetadataOpen) return

    setBulkMetaApplyVendor(false)
    setBulkMetaVendorName(null)
    setBulkMetaApplyDocType(false)
    setBulkMetaDocType('')
    setBulkMetaApplyLocation(false)
    setBulkMetaLocationId('')
    setBulkMetaApplyMemo(false)
    setBulkMetaMemo('')
    setBulkMetaApplyLineCategory(false)
    setBulkMetaCategoryId(null)
    setBulkMetaApplyTerms(false)
    setBulkMetaTermId('')

    let cancelled = false
    async function load() {
      setBulkMetaQboLoading(true)
      try {
        const [v, loc, cats, terms] = await Promise.all([
          getVendors(),
          getLocations(),
          getCategories(),
          getPaymentTerms(false),
        ])
        if (!cancelled) {
          setBulkMetaVendors(v.filter((x) => x.active))
          setBulkMetaLocations(loc.filter((x) => x.active))
          setBulkMetaCategories(cats)
          setBulkMetaTerms(terms.filter((t) => t.active))
        }
      } catch (err) {
        console.error(err)
        if (!cancelled) {
          toast.error('Failed to load QBO vendors, locations, categories, or payment terms for bulk edit')
        }
      } finally {
        if (!cancelled) setBulkMetaQboLoading(false)
      }
    }
    load()
    return () => {
      cancelled = true
    }
  }, [bulkMetadataOpen])

  async function handleApprove(
    doc: Document,
    options?: { overrideNonPayable?: boolean; overrideQboDuplicate?: boolean },
  ) {
    if (!canEdit) return
    setIsApproving(true)
    setApprovalError(null)
    try {
      const approved = await approveDocument(doc.id, options)
      setDocuments((prev) => prev.filter((d) => d.id !== doc.id))
      setTotal((prev) => prev - 1)
      usePendingReviewStore.getState().adjustCount(-1)
      scheduleSilentPendingCohortRefresh()

      if (selectedDocument?.id === doc.id) {
        bumpDocumentHydrationGen()
        setSelectedDocument(approved)
      }
    } catch (err: unknown) {
      setApprovalError(formatApiError(err, 'Failed to approve document'))
      console.error(err)
    } finally {
      setIsApproving(false)
    }
  }

  async function handleOverwriteApprovedDuplicate(doc: Document) {
    if (!canEdit) return
    setIsOverwriting(true)
    setOverwriteError(null)
    try {
      const approved = await overwriteApprovedDuplicate(doc.id)
      setDocuments((prev) => prev.filter((d) => d.id !== doc.id))
      setTotal((prev) => prev - 1)
      usePendingReviewStore.getState().adjustCount(-1)
      scheduleSilentPendingCohortRefresh()
      toast.success('Existing QuickBooks bill updated and document approved.')

      if (selectedDocument?.id === doc.id) {
        bumpDocumentHydrationGen()
        setSelectedDocument(approved)
      }
    } catch (err: unknown) {
      setOverwriteError(formatApiError(err, 'Failed to overwrite existing QuickBooks bill'))
      console.error(err)
    } finally {
      setIsOverwriting(false)
    }
  }

  async function handleUnapprove(doc: Document) {
    if (!confirm('Move this document back to Review tab? Note: The bill/credit will remain in QuickBooks and must be deleted manually if needed.')) return

    try {
      const updated = await unapproveDocument(doc.id)
      setDocuments((prev) => prev.filter((d) => d.id !== doc.id))
      setTotal((prev) => prev - 1)
      usePendingReviewStore.getState().adjustCount(1)
      setActiveTab('pending_review')
      bumpDocumentHydrationGen()
      setSelectedDocument(updated)
      setDocumentPreviewHydrating(true)
      void getDocument(updated.id)
        .then((full) => {
          setSelectedDocument(full)
          setDocumentPreviewHydrating(false)
        })
        .catch(() => setDocumentPreviewHydrating(false))
    } catch (err: unknown) {
      toast.error(formatApiError(err, 'Failed to unapprove document'))
      console.error(err)
    }
  }

  async function handleDelete(doc: Document) {
    if (!canEdit) return
    if (!confirm('Are you sure you want to archive this document?')) return

    try {
      await deleteDocument(doc.id)
      setDocuments((prev) => prev.filter((d) => d.id !== doc.id))
      setTotal((prev) => prev - 1)
      if (activeTab === 'pending_review') {
        usePendingReviewStore.getState().adjustCount(-1)
      }
      scheduleSilentPendingCohortRefresh()
      if (selectedDocument?.id === doc.id) {
        bumpDocumentHydrationGen()
        setSelectedDocument(null)
      }
    } catch (err) {
      toast.error('Failed to delete document')
      console.error(err)
    }
  }

  async function handleRestore(doc: Document) {
    try {
      const updated = await restoreDocument(doc.id)
      setDocuments((prev) => prev.filter((d) => d.id !== doc.id))
      setTotal((prev) => prev - 1)
      usePendingReviewStore.getState().adjustCount(1)
      setActiveTab('pending_review')
      bumpDocumentHydrationGen()
      setSelectedDocument(updated)
      setDocumentPreviewHydrating(true)
      void getDocument(updated.id)
        .then((full) => {
          setSelectedDocument(full)
          setDocumentPreviewHydrating(false)
        })
        .catch(() => setDocumentPreviewHydrating(false))
    } catch (err) {
      toast.error('Failed to restore document')
      console.error(err)
    }
  }

  async function handlePermanentDelete(docIds: number[]) {
    if (!confirm(`Are you sure you want to permanently delete ${docIds.length} document(s)? This cannot be undone.`)) return

    try {
      await Promise.all(docIds.map(id => permanentlyDeleteDocument(id)))
      setDocuments((prev) => prev.filter((d) => !docIds.includes(d.id)))
      setTotal((prev) => prev - docIds.length)
      scheduleSilentPendingCohortRefresh()
      setSelectedIds(new Set())
      setFullCohortSelected(false)
      cohortExtraDocsRef.current.clear()
      selectionAnchorIndexRef.current = null
      if (selectedDocument && docIds.includes(selectedDocument.id)) {
        bumpDocumentHydrationGen()
        setSelectedDocument(null)
      }
    } catch (err) {
      toast.error('Failed to permanently delete documents')
      console.error(err)
    }
  }

  async function handleBulkArchive() {
    if (!canEdit) return
    const selectedCount = selectedIds.size
    if (selectedCount === 0) return
    if (!confirm(`Archive ${selectedCount} document(s)?`)) return

    try {
      const docIds = Array.from(selectedIds)
      await Promise.all(docIds.map(id => deleteDocument(id)))
      setDocuments((prev) => prev.filter((d) => !docIds.includes(d.id)))
      setTotal((prev) => prev - docIds.length)
      if (activeTab === 'pending_review') {
        usePendingReviewStore.getState().adjustCount(-docIds.length)
      }
      scheduleSilentPendingCohortRefresh()
      setSelectedIds(new Set())
      setFullCohortSelected(false)
      cohortExtraDocsRef.current.clear()
      selectionAnchorIndexRef.current = null
      if (selectedDocument && docIds.includes(selectedDocument.id)) {
        bumpDocumentHydrationGen()
        setSelectedDocument(null)
      }
      toast.success(`Archived ${selectedCount} document(s)`)
    } catch (err) {
      toast.error('Failed to archive documents')
      console.error(err)
    }
  }

  async function handleBulkRestore() {
    const selectedCount = selectedIds.size
    if (selectedCount === 0) return
    
    const confirmMsg = activeTab === 'approved' 
      ? `Move ${selectedCount} document(s) back to Review tab? Note: Bills/credits will remain in QuickBooks.`
      : `Restore ${selectedCount} document(s) to Review tab?`
    
    if (!confirm(confirmMsg)) return

    try {
      const docIds = Array.from(selectedIds)
      if (activeTab === 'approved') {
        await Promise.all(docIds.map(id => unapproveDocument(id)))
      } else {
        await Promise.all(docIds.map(id => restoreDocument(id)))
      }
      setDocuments((prev) => prev.filter((d) => !docIds.includes(d.id)))
      setTotal((prev) => prev - docIds.length)
      usePendingReviewStore.getState().adjustCount(docIds.length)
      setSelectedIds(new Set())
      setFullCohortSelected(false)
      cohortExtraDocsRef.current.clear()
      selectionAnchorIndexRef.current = null
      setActiveTab('pending_review')
      if (docIds.length === 1) {
        const restoredId = docIds[0]
        if (restoredId != null) {
          bumpDocumentHydrationGen()
          setDocumentPreviewHydrating(true)
          void getDocument(restoredId)
            .then((full) => {
              setSelectedDocument(full)
              setDocumentPreviewHydrating(false)
            })
            .catch(() => setDocumentPreviewHydrating(false))
        }
      } else if (selectedDocument && docIds.includes(selectedDocument.id)) {
        bumpDocumentHydrationGen()
        setSelectedDocument(null)
      }
      toast.success(`Restored ${selectedCount} document(s) to Review`)
    } catch (err) {
      toast.error('Failed to restore documents')
      console.error(err)
    }
  }

  function toggleSelectAll() {
    const allRowsSelected =
      uniqueListDocIds.length > 0 && uniqueListDocIds.every((id) => selectedIds.has(id))

    if (fullCohortSelected || allRowsSelected) {
      setSelectedIds(new Set())
      setFullCohortSelected(false)
      cohortExtraDocsRef.current.clear()
      selectionAnchorIndexRef.current = null
      return
    }
    setSelectedIds(new Set(uniqueListDocIds))
    setFullCohortSelected(false)
    cohortExtraDocsRef.current.clear()
    selectionAnchorIndexRef.current = uniqueListDocIds.length > 0 ? 0 : null
  }

  async function handleSelectAllInView() {
    if (total <= 0) return
    const seq = ++selectAllCohortSeqRef.current
    setIsSelectingAllInView(true)
    try {
      const base = getListQueryParams()
      const first = await getDocuments({
        ...base,
        page: 1,
        page_size: LIST_PAGE_SIZE,
      })
      if (seq !== selectAllCohortSeqRef.current) return

      const extra = new Map<number, Document>()
      for (const d of first.documents) {
        extra.set(d.id, d)
      }
      const totalCount = first.total
      const totalPages = Math.max(1, Math.ceil(totalCount / LIST_PAGE_SIZE))

      for (let p = 2; p <= totalPages; p++) {
        const data = await getDocuments({
          ...base,
          page: p,
          page_size: LIST_PAGE_SIZE,
        })
        if (seq !== selectAllCohortSeqRef.current) return
        for (const d of data.documents) {
          extra.set(d.id, d)
        }
      }

      if (seq !== selectAllCohortSeqRef.current) return
      cohortExtraDocsRef.current = extra
      setSelectedIds(new Set(extra.keys()))
      setFullCohortSelected(true)
    } catch (err) {
      console.error(err)
      if (seq === selectAllCohortSeqRef.current) {
        toast.error('Failed to select all documents in this view')
      }
    } finally {
      if (seq === selectAllCohortSeqRef.current) {
        setIsSelectingAllInView(false)
      }
    }
  }

  function handleQboVendorCreated(vendor: QBOVendor) {
    setQboActiveVendors((prev) => {
      const rest = prev.filter((v) => v.id !== vendor.id)
      return vendor.active ? [...rest, vendor] : rest
    })
  }

  function handleDocumentUpdate(updated: Document) {
    const prevRow = documentsRef.current.find((d) => d.id === updated.id)
    const cohortMayShift =
      activeTabRef.current === 'pending_review' &&
      !!prevRow &&
      (prevRow.vendor_name !== updated.vendor_name ||
        prevRow.reference_number !== updated.reference_number ||
        prevRow.status !== updated.status)
    setDocuments((prev) => prev.map((d) => (d.id === updated.id ? updated : d)))
    // Do not reopen the panel if the user closed it (or navigated away) before a
    // debounced auto-save finished; still merge the saved doc into the table above.
    setSelectedDocument((current) =>
      current?.id === updated.id ? updated : current
    )
    if (cohortMayShift) {
      scheduleSilentPendingCohortRefresh()
    }
  }

  async function handleRefreshAllCategories() {
    const sourceDocs =
      selectedIds.size > 0 ? getSelectedDocuments() : documents
    if (sourceDocs.length === 0) {
      if (selectedIds.size > 0) {
        toast.error('Could not load row data for selected documents. Try reloading the page.')
      }
      return
    }

    const eligibleDocs = sourceDocs.filter(
      (doc) => doc.vendor_name && doc.line_items && doc.line_items.length > 0,
    )
    if (eligibleDocs.length === 0) {
      toast.error(
        selectedIds.size > 0
          ? 'No selected documents have both a vendor and line items to refresh'
          : 'No documents with vendors and line items to refresh',
      )
      return
    }

    const eligibleIds = eligibleDocs.map((d) => d.id)
    const { jobDocumentIds, syncOpenDocumentId } = partitionOpenDocumentFromBulkIds(
      eligibleIds,
      selectedDocument?.id,
    )

    try {
      setIsStartingRefreshCategories(true)

      if (syncOpenDocumentId != null) {
        const updated = await refreshDocumentCategories(syncOpenDocumentId)
        handleDocumentUpdate(updated)
      }

      if (jobDocumentIds.length > 0) {
        const response = await startBulkRefreshCategoriesJob(jobDocumentIds)
        registerServerJob({
          id: response.job_id,
          job_type: 'bulk_refresh_categories',
          status: 'pending',
          total_items: response.total_items,
          processed_items: 0,
          successful_items: 0,
          failed_items: 0,
          created_at: new Date().toISOString(),
        })
      } else if (syncOpenDocumentId != null) {
        toast.success('Refreshed categories for the open document.')
      }
    } catch (err: unknown) {
      toast.error(formatApiError(err, 'Failed to start category refresh'))
      console.error(err)
    } finally {
      setIsStartingRefreshCategories(false)
    }
  }

  async function handleMatchVendors() {
    try {
      setIsMatchingVendors(true)
      const result = await bulkMatchVendors()

      if (result.updated_count > 0) {
        setDocuments((prev) => {
          const updatedMap = new Map(result.updated_docs.map(d => [d.id, d.vendor_name]))
          return prev.map(doc => {
            const newVendorName = updatedMap.get(doc.id)
            if (newVendorName) {
              return { ...doc, vendor_name: newVendorName }
            }
            return doc
          })
        })

        if (selectedDocument) {
          const updatedVendor = result.updated_docs.find(d => d.id === selectedDocument.id)
          if (updatedVendor) {
            setSelectedDocument({ ...selectedDocument, vendor_name: updatedVendor.vendor_name })
          }
        }
      }

      toast.success(`Matched ${result.updated_count} of ${result.total_reviewed} vendors to QBO`)
    } catch (err: unknown) {
      toast.error(formatApiError(err, 'Failed to match vendors'))
      console.error(err)
    } finally {
      setIsMatchingVendors(false)
    }
  }

  async function handleBulkApprove() {
    if (!canEdit) return
    const selectedDocs = getSelectedDocuments()
    const eligibleDocs = selectedDocs.filter(d => !hasBlockingApproveFlag(d))

    if (eligibleDocs.length === 0) {
      toast.error(
        'No eligible documents to approve. Resolve blocking flags (duplicate in QBO, unknown vendor, missing fields, unallocated shared costs, etc.) first. Total mismatch is the only confirm-only warning.'
      )
      return
    }

    const skippedBlocking = selectedDocs.length - eligibleDocs.length
    const mismatchConfirmCount = eligibleDocs.filter(docNeedsTotalMismatchSendConfirm).length

    const confirmParts: string[] = [
      `Approve ${eligibleDocs.length} document(s) and send to QBO in background?`,
    ]
    if (mismatchConfirmCount > 0) {
      confirmParts.push(
        '',
        mismatchConfirmCount === 1
          ? '1 of these documents has a total mismatch concern (extracted vs. entered total). Ignore this warning and send anyway?'
          : `${mismatchConfirmCount} of these documents have a total mismatch concern. Ignore these warnings and send anyway?`
      )
    }
    if (skippedBlocking > 0) {
      confirmParts.push(
        '',
        `${skippedBlocking} other selected document(s) will be skipped because they have blocking flags.`
      )
    }

    if (!confirm(confirmParts.join('\n'))) return

    const eligibleIds = eligibleDocs.map(d => d.id)
    const eligibleIdSet = new Set(eligibleIds)

    try {
      setIsBulkApproving(true)

      const response = await startBulkApprove(eligibleIds)
      
      setDocuments((prev) => prev.filter(d => !eligibleIdSet.has(d.id)))
      setTotal((prev) => prev - eligibleIds.length)
      usePendingReviewStore.getState().adjustCount(-eligibleIds.length)
      scheduleSilentPendingCohortRefresh()

      if (selectedDocument && eligibleIdSet.has(selectedDocument.id)) {
        bumpDocumentHydrationGen()
        setSelectedDocument(null)
      }

      registerServerJob({
        id: response.job_id,
        job_type: 'bulk_approve',
        status: 'pending',
        total_items: response.total_items,
        processed_items: 0,
        successful_items: 0,
        failed_items: 0,
        created_at: new Date().toISOString(),
      })

      setSelectedIds(new Set())
      setFullCohortSelected(false)
      cohortExtraDocsRef.current.clear()
      selectionAnchorIndexRef.current = null
    } catch (err: unknown) {
      toast.error(formatApiError(err, 'Failed to start bulk approve'))
      console.error(err)
    } finally {
      setIsBulkApproving(false)
    }
  }

  useEffect(() => {
    bulkUploadMergedDocIdsRef.current = new Set()
  }, [bulkUploadIdsKey])

  useEffect(() => {
    if (activeTab !== 'pending_review') return

    const jobs = Object.values(useBackgroundJobsStore.getState().serverById)
      .filter((r) => r.job.job_type === 'bulk_upload')
      .map((r) => r.job)
    if (jobs.length === 0) return

    const shouldMergeUpload = (j: BackgroundJob) =>
      j.job_type === 'bulk_upload' &&
      (j.status === 'running' ||
        j.status === 'pending' ||
        j.status === 'completed' ||
        j.status === 'failed')

    let completedUploadNeedsCohortRefresh = false

    for (const updatedJob of jobs) {
      if (!shouldMergeUpload(updatedJob)) continue

      if (
        updatedJob.status === 'completed' &&
        updatedJob.result_data?.kind === 'amazon_report' &&
        updatedJob.result_data.summary &&
        !amazonReportBulkToastShownRef.current.has(updatedJob.id)
      ) {
        amazonReportBulkToastShownRef.current.add(updatedJob.id)
        const s = updatedJob.result_data.summary
        let msg = `Amazon report: ${s.created} charges created, ${s.updated} updated, ${s.unchanged} unchanged, ${s.skipped_approved} already approved`
        if (s.skipped_deleted) {
          msg += `, ${s.skipped_deleted} previously deleted`
        }
        if ((s.errors?.length ?? 0) > 0) {
          msg += `, ${s.errors!.length} errors`
        }
        toast.success(msg)
        completedUploadNeedsCohortRefresh = true
      }

      if (
        updatedJob.status === 'completed' &&
        (updatedJob.result_data?.skipped_docs?.length ?? 0) > 0 &&
        !bulkUploadSkippedToastShownRef.current.has(updatedJob.id)
      ) {
        bulkUploadSkippedToastShownRef.current.add(updatedJob.id)
        const skipped = updatedJob.result_data!.skipped_docs!
        for (const s of skipped) {
          const tab = skippedDocQueueTab(s)
          toast.warning(skippedDuplicateToastMessage(s), {
            title: 'Duplicate invoices skipped',
            persistent: true,
            action: {
              label: skippedDuplicateToastActionLabel(tab),
              onClick: () => {
                setSearchParams((prev) => {
                  const next = new URLSearchParams(prev)
                  next.set('doc', String(s.existing_document_id))
                  return next
                })
              },
            },
          })
        }
      }

      if (!updatedJob.result_data?.uploaded_docs?.length) {
        if (updatedJob.status === 'completed' || updatedJob.status === 'failed') {
          completedUploadNeedsCohortRefresh = true
        }
        continue
      }
      const uploaded = updatedJob.result_data.uploaded_docs
      const newIds = uploaded
        .map((u) => u.id)
        .filter((id) => id != null && !bulkUploadMergedDocIdsRef.current.has(id))
      if (newIds.length === 0) continue
      for (const id of newIds) {
        bulkUploadMergedDocIdsRef.current.add(id)
      }
      void (async () => {
        try {
          const fetched = await Promise.all(newIds.map((id) => getDocument(id)))
          const existing = new Set(documentsRef.current.map((d) => d.id))
          const toAdd = fetched.filter((d) => !existing.has(d.id))
          if (toAdd.length > 0) {
            usePendingReviewStore.getState().adjustCount(toAdd.length)
            setDocuments((prev) => [...toAdd, ...prev])
            setTotal((t) => t + toAdd.length)
          }
          scheduleSilentPendingCohortRefresh()
        } catch (mergeErr) {
          console.error('Failed to merge uploaded documents:', mergeErr)
        }
      })()
      if (updatedJob.status === 'completed' || updatedJob.status === 'failed') {
        completedUploadNeedsCohortRefresh = true
      }
    }

    if (completedUploadNeedsCohortRefresh) {
      scheduleSilentPendingCohortRefresh()
    }
  }, [bulkUploadMergeKey, activeTab, scheduleSilentPendingCohortRefresh])

  useEffect(() => {
    if (activeTab !== 'pending_review') return
    const jobs = Object.values(useBackgroundJobsStore.getState().serverById)
      .filter((r) => r.job.job_type === 'bulk_reprocess')
      .map((r) => r.job)
    for (const job of jobs) {
      if (job.status !== 'running' && job.status !== 'completed') continue
      mergeDocumentsFromBackgroundJob(job, 'reprocessed_docs')
    }
  }, [bulkReprocessJobsKey, activeTab, mergeDocumentsFromBackgroundJob])

  useEffect(() => {
    if (activeTab !== 'pending_review') return
    const jobs = Object.values(useBackgroundJobsStore.getState().serverById)
      .filter((r) => r.job.job_type === 'bulk_allocate_shared_costs')
      .map((r) => r.job)
    for (const job of jobs) {
      if (job.status !== 'running' && job.status !== 'completed') continue
      mergeDocumentsFromBackgroundJob(job, 'updated_docs')
    }
  }, [bulkAllocateMergeKey, activeTab, mergeDocumentsFromBackgroundJob])

  useEffect(() => {
    if (activeTab !== 'pending_review') return
    const jobs = Object.values(useBackgroundJobsStore.getState().serverById)
      .filter((r) => r.job.job_type === 'bulk_refresh_categories')
      .map((r) => r.job)
    for (const job of jobs) {
      if (job.status !== 'running' && job.status !== 'completed') continue
      mergeDocumentsFromBackgroundJob(job, 'updated_docs')
    }
  }, [bulkRefreshCategoriesMergeKey, activeTab, mergeDocumentsFromBackgroundJob])

  function getEligibleSelectedCount(): number {
    return getSelectedDocuments().filter((d) => !hasBlockingApproveFlag(d)).length
  }

  async function handleCheckDuplicates() {
    const ids = Array.from(selectedIds)
    if (ids.length === 0) {
      toast.error('Select one or more documents to check for duplicates in QuickBooks')
      return
    }
    try {
      setIsCheckingDuplicates(true)
      setDuplicateCheckResult(null)

      const result = await checkQboDuplicates(ids)

      await loadDocuments()
      void loadFlagFacets()

      if (result.checked_count === 0) {
        toast.error(
          'None of the selected documents have both vendor and reference set, so QuickBooks was not queried.',
        )
        return
      }

      if (result.duplicate_count > 0) {
        setDuplicateCheckResult(result)
      } else {
        toast.success(
          `Checked ${result.checked_count} document(s) — no duplicates found in QuickBooks`,
        )
      }
    } catch (err: unknown) {
      toast.error(formatApiError(err, 'Failed to check for duplicates'))
      console.error(err)
    } finally {
      setIsCheckingDuplicates(false)
    }
  }

  function emailRuleIdFromModalSelect(): number | undefined {
    if (!processingHintsRuleId) return undefined
    const n = Number.parseInt(processingHintsRuleId, 10)
    return Number.isFinite(n) ? n : undefined
  }

  function closeProcessingHintsModal() {
    setProcessingHintsModal(null)
    if (fileInputRef.current) {
      fileInputRef.current.value = ''
    }
  }

  function openReprocessHintsModal() {
    if (selectedIds.size === 0) return
    setProcessingHintsModal({ kind: 'reprocess' })
  }

  function queuePdfUploadWithHints(files: File[]) {
    const allowed = files.filter((f) => {
      const n = f.name.toLowerCase()
      return n.endsWith('.pdf') || n.endsWith('.csv')
    })

    if (allowed.length === 0) {
      toast.error('Only PDF or CSV files are supported')
      return
    }

    if (allowed.length < files.length) {
      toast.error(`${files.length - allowed.length} unsupported file(s) were skipped`)
    }

    setProcessingHintsModal({ kind: 'upload', files: allowed })
  }

  async function confirmProcessingHintsModal() {
    const hintRuleId = emailRuleIdFromModalSelect()

    if (processingHintsModal?.kind === 'reprocess') {
      const selectedCount = selectedIds.size
      if (selectedCount === 0) {
        closeProcessingHintsModal()
        return
      }
      const selectedDocs = documents.filter((d) => selectedIds.has(d.id))
      const amazonReportIds = selectedDocs
        .filter((d) => d.source_type === 'amazon_report')
        .map((d) => d.id)
      const reprocessIds = selectedDocs
        .filter((d) => d.source_type !== 'amazon_report')
        .map((d) => d.id)
      if (reprocessIds.length === 0) {
        toast.error('Amazon Business report documents cannot be re-extracted.')
        closeProcessingHintsModal()
        return
      }
      if (amazonReportIds.length > 0) {
        toast.warning(
          `Skipped ${amazonReportIds.length} Amazon Business report document(s); re-running extraction for the rest.`,
        )
      }
      try {
        setIsReprocessing(true)
        setReprocessResult(null)

        const response = await startBulkReprocess(
          reprocessIds,
          hintRuleId != null ? { emailRuleId: hintRuleId } : undefined,
        )

        registerServerJob({
          id: response.job_id,
          job_type: 'bulk_reprocess',
          status: 'pending',
          total_items: response.total_items,
          processed_items: 0,
          successful_items: 0,
          failed_items: 0,
          created_at: new Date().toISOString(),
        })
        closeProcessingHintsModal()
      } catch (err: unknown) {
        toast.error(formatApiError(err, 'Failed to start re-run extraction'))
        console.error(err)
      } finally {
        setIsReprocessing(false)
      }
      return
    }

    if (processingHintsModal?.kind === 'upload') {
      const uploadFiles = processingHintsModal.files
      const csvFiles = uploadFiles.filter((f) => f.name.toLowerCase().endsWith('.csv'))
      const pdfFiles = uploadFiles.filter((f) => f.name.toLowerCase().endsWith('.pdf'))
      try {
        setUploading(true)

        for (const file of csvFiles) {
          const result = await uploadDocument(
            file,
            hintRuleId != null ? { emailRuleId: hintRuleId } : undefined,
          )
          if (isAmazonReportUploadResponse(result)) {
            const s = result.summary
            let msg = `Amazon report: ${s.created} charges created, ${s.updated} updated, ${s.unchanged} unchanged, ${s.skipped_approved} already approved`
            if (s.skipped_deleted) {
              msg += `, ${s.skipped_deleted} previously deleted`
            }
            if (s.errors.length > 0) {
              msg += `, ${s.errors.length} errors`
            }
            toast.success(msg)
            scheduleSilentPendingCohortRefresh()
          } else {
            const existing = new Set(documentsRef.current.map((d) => d.id))
            if (!existing.has(result.id)) {
              usePendingReviewStore.getState().adjustCount(1)
              setDocuments((prev) => [result, ...prev])
              setTotal((t) => t + 1)
            }
            scheduleSilentPendingCohortRefresh()
          }
        }

        if (pdfFiles.length > 0) {
          const response = await startBulkUpload(
            pdfFiles,
            hintRuleId != null ? { emailRuleId: hintRuleId } : undefined,
          )

          registerServerJob({
            id: response.job_id,
            job_type: 'bulk_upload',
            status: 'pending',
            total_items: response.total_items,
            processed_items: 0,
            successful_items: 0,
            failed_items: 0,
            created_at: new Date().toISOString(),
          })
        }

        closeProcessingHintsModal()
      } catch (err: unknown) {
        toast.error(formatApiError(err, 'Failed to start upload'))
        console.error(err)
      } finally {
        setUploading(false)
        if (fileInputRef.current) {
          fileInputRef.current.value = ''
        }
      }
    }
  }

  async function handleBulkCreateVendor(displayName: string): Promise<SelectOption | null> {
    try {
      const v = await createVendor(displayName)
      handleQboVendorCreated(v)
      setBulkMetaVendors((prev) => {
        const rest = prev.filter((x) => x.id !== v.id)
        return [...rest, v]
      })
      setBulkMetaVendorName(v.display_name)
      return { id: v.display_name, label: v.display_name }
    } catch (err) {
      toast.error(formatApiError(err, 'Failed to create vendor'))
      throw err
    }
  }

  async function handleBulkMetadataSubmit() {
    const applies =
      bulkMetaApplyVendor ||
      bulkMetaApplyDocType ||
      bulkMetaApplyLocation ||
      bulkMetaApplyMemo ||
      bulkMetaApplyLineCategory ||
      bulkMetaApplyTerms
    if (!applies) {
      toast.error('Select at least one field to update')
      return
    }
    if (bulkMetaApplyVendor && !bulkMetaVendorName?.trim()) {
      toast.error('Choose a vendor, or uncheck Vendor')
      return
    }
    if (bulkMetaApplyDocType && !bulkMetaDocType) {
      toast.error('Choose Bill or Vendor credit, or uncheck Type')
      return
    }
    if (bulkMetaApplyLocation && !bulkMetaLocationId) {
      toast.error('Choose a location, or uncheck Location')
      return
    }
    if (bulkMetaApplyLineCategory && !bulkMetaCategoryId?.trim()) {
      toast.error('Choose a line-item category, or uncheck Line item category')
      return
    }

    const n = selectedIds.size
    const toastId = startSynthetic(`Updating ${n} document(s)…`)

    try {
      setBulkMetaSaving(true)
      const body: BulkMetadataUpdateRequest = {
        document_ids: Array.from(selectedIds),
      }
      if (bulkMetaApplyVendor) body.vendor_name = bulkMetaVendorName!.trim()
      if (bulkMetaApplyDocType) body.doc_type = bulkMetaDocType as 'bill' | 'vendor_credit'
      if (bulkMetaApplyLocation) {
        const loc = bulkMetaLocations.find((l) => l.id === bulkMetaLocationId)
        body.location_id = bulkMetaLocationId
        body.location = loc?.name ?? ''
      }
      if (bulkMetaApplyMemo) body.memo = bulkMetaMemo
      if (bulkMetaApplyLineCategory && bulkMetaCategoryId) {
        const cat = bulkMetaCategories.find((c) => c.id === bulkMetaCategoryId)
        body.line_item_category_id = bulkMetaCategoryId
        body.line_item_category_name = cat?.name ?? ''
      }
      if (bulkMetaApplyTerms) {
        const tid = bulkMetaTermId.trim()
        body.qbo_sales_term_id = tid.length > 0 ? tid : null
      }

      const updatedDocs = await bulkUpdateDocumentMetadata(body)
      setDocuments((prev) => {
        const m = new Map(updatedDocs.map((d) => [d.id, d]))
        return prev.map((d) => m.get(d.id) ?? d)
      })
      if (activeTabRef.current === 'pending_review') {
        scheduleSilentPendingCohortRefresh()
      }
      if (selectedDocument && updatedDocs.some((d) => d.id === selectedDocument.id)) {
        const u = updatedDocs.find((d) => d.id === selectedDocument.id)
        if (u) setSelectedDocument(u)
      }
      const labels: string[] = []
      if (bulkMetaApplyVendor) labels.push('vendor')
      if (bulkMetaApplyDocType) labels.push('type')
      if (bulkMetaApplyLocation) labels.push('location')
      if (bulkMetaApplyMemo) labels.push('memo')
      if (bulkMetaApplyLineCategory) labels.push('line categories')
      if (bulkMetaApplyTerms) labels.push('terms')
      const msg = `Updated ${labels.join(', ')} on ${updatedDocs.length} document(s)`
      completeSynthetic(toastId, msg)
      setBulkMetadataOpen(false)
      setSelectedIds(new Set())
      setFullCohortSelected(false)
      cohortExtraDocsRef.current.clear()
      selectionAnchorIndexRef.current = null
    } catch (err: unknown) {
      const detail = getApiErrorDetail(err)
      const errText =
        typeof detail === 'string'
          ? detail
          : detail
            ? JSON.stringify(detail)
            : 'Failed to update documents'
      failSynthetic(toastId, errText)
      console.error(err)
    } finally {
      setBulkMetaSaving(false)
    }
  }

  async function handleBulkAllocateSharedCosts() {
    const selected = getSelectedDocuments()
    const eligible = selected.filter(
      (d) =>
        d.status === 'pending_review' &&
        d.has_unallocated_shared_costs === true,
    )
    const skippedCount = selected.length - eligible.length
    if (eligible.length === 0) {
      toast.error(
        'No selected documents are eligible. Each must be pending review and have unallocated shared-cost lines.',
      )
      return
    }
    const skipNote =
      skippedCount > 0
        ? ` ${skippedCount} selected document(s) will be skipped (wrong status or no shared costs to allocate).`
        : ''
    if (
      !confirm(
        `Allocate shared costs on ${eligible.length} document(s)?${skipNote} Line items will be updated on each document.`,
      )
    ) {
      return
    }

    const eligibleIds = eligible.map((d) => d.id)
    const { jobDocumentIds, syncOpenDocumentId } = partitionOpenDocumentFromBulkIds(
      eligibleIds,
      selectedDocument?.id,
    )

    try {
      setIsStartingBulkAllocate(true)

      if (syncOpenDocumentId != null) {
        const updated = await allocateSharedCosts(syncOpenDocumentId)
        handleDocumentUpdate(updated)
      }

      if (jobDocumentIds.length > 0) {
        const response = await startBulkAllocateSharedCosts(jobDocumentIds)
        registerServerJob({
          id: response.job_id,
          job_type: 'bulk_allocate_shared_costs',
          status: 'pending',
          total_items: response.total_items,
          processed_items: 0,
          successful_items: 0,
          failed_items: 0,
          created_at: new Date().toISOString(),
        })
      }

      if (syncOpenDocumentId != null) {
        toast.success('Allocated shared costs on the open document.')
      }
      setBulkMetadataOpen(false)
    } catch (err: unknown) {
      const detail = getApiErrorDetail(err)
      const errText =
        typeof detail === 'string'
          ? detail
          : detail
            ? JSON.stringify(detail)
            : 'Failed to allocate shared costs'
      toast.error(errText)
      console.error(err)
    } finally {
      setIsStartingBulkAllocate(false)
    }
  }

  async function handleUpload(event: React.ChangeEvent<HTMLInputElement>) {
    const files = event.target.files
    if (!files || files.length === 0) return
    queuePdfUploadWithHints(Array.from(files))
  }

  function handleDragEnter(e: React.DragEvent) {
    if (processingHintsModal) return
    e.preventDefault()
    e.stopPropagation()
    dragCounterRef.current++
    if (e.dataTransfer.items && e.dataTransfer.items.length > 0) {
      setIsDraggingOver(true)
    }
  }

  function handleDragLeave(e: React.DragEvent) {
    if (processingHintsModal) return
    e.preventDefault()
    e.stopPropagation()
    dragCounterRef.current--
    if (dragCounterRef.current === 0) {
      setIsDraggingOver(false)
    }
  }

  function handleDragOver(e: React.DragEvent) {
    if (processingHintsModal) return
    e.preventDefault()
    e.stopPropagation()
  }

  async function handleDrop(e: React.DragEvent) {
    e.preventDefault()
    e.stopPropagation()
    setIsDraggingOver(false)
    dragCounterRef.current = 0

    if (uploading || processingHintsModal) return

    const files = Array.from(e.dataTransfer.files)
    if (files.length > 0) {
      queuePdfUploadWithHints(files)
    }
  }

  function handleFilterChange(field: keyof Filters, value: string) {
    setListPage(1)
    setFilters((prev) => ({ ...prev, [field]: value }))
  }

  function clearFilters() {
    setListPage(1)
    setFilters(emptyFilters)
  }

  const hasActiveAmountFilter = Boolean(apiAmountParams.amount)
  const hasActiveFilters =
    Object.entries(filters).some(([key, v]) => {
      if (key === 'amount_op') return false
      if (key === 'amount') return hasActiveAmountFilter
      return v !== ''
    })

  function getFilterInputClass(value: string): string {
    const baseClass = 'input-sm'
    if (value) {
      return `${baseClass} ring-2 ring-gray-300 bg-gray-50`
    }
    return baseClass
  }

  function getFilterSelectClass(value: string): string {
    const baseClass = 'input-sm select'
    if (value) {
      return `${baseClass} ring-2 ring-gray-300 bg-gray-50`
    }
    return baseClass
  }

  const filterPills = useMemo(
    () => generateDocReviewFilterPills(filters, hasActiveAmountFilter),
    [filters, hasActiveAmountFilter],
  )

  function handleRemoveFilterPill(pillId: string) {
    setListPage(1)
    setFilters((prev) => removeDocReviewFilterPill(prev, pillId))
  }

  useEffect(() => {
    if (activeTab === 'pending_review' || !hasActiveFilters) {
      setTabUnfilteredTotal(null)
      return
    }
    let cancelled = false
    void getDocuments({ status: activeTab, page: 1, page_size: 1 })
      .then((data) => {
        if (!cancelled) {
          setTabUnfilteredTotal(data.total)
        }
      })
      .catch((err) => {
        console.error(err)
        if (!cancelled) {
          setTabUnfilteredTotal(null)
        }
      })
    return () => {
      cancelled = true
    }
  }, [activeTab, hasActiveFilters, refreshTrigger])

  const pendingPeerDuplicateIds = useMemo(
    () => buildPendingPeerDuplicateIds(documents),
    [documents],
  )

  function isDuplicate(doc: Document): boolean {
    // Approved vendor+ref collision (always server).
    if (doc.duplicate_matches_approved === true) return true
    // Pending peer duplicate among loaded rows (client; same rules as pendingPeerDuplicateIds).
    if (pendingPeerDuplicateIds.has(doc.id)) return true
    // Peer duplicate where the sibling is not on this page (server only); omit duplicate_in_pending
    // here — it stayed true after a same-page sibling was removed until silent cohort refresh.
    return doc.duplicate_peer_pending === true
  }

  function hasQboDuplicate(doc: Document): boolean {
    return doc.needs_review_flags?.some((f) => f === 'qbo_duplicate') ?? false
  }

  function matchesApprovedReference(doc: Document): boolean {
    return doc.duplicate_matches_approved === true
  }

  /** Same vendor+ref as another pending row (warn); not QBO dup or approved collision. */
  function hasPendingPeerDuplicateOnly(doc: Document): boolean {
    return isDuplicate(doc) && !hasQboDuplicate(doc) && !matchesApprovedReference(doc)
  }

  function hasUnknownVendor(doc: Document): boolean {
    if (!doc.vendor_name?.trim()) return false
    if (qboActiveVendors.length === 0) return false
    return !isDocVendorKnownInQbo(doc.vendor_name, qboActiveVendors)
  }

  /** Bills and vendor credits must have at least one line item before QBO (matches API and detail panel). */
  function isBillOrVendorCreditWithoutLineItems(doc: Document): boolean {
    if (!docTypeRequiresLineItems(doc.doc_type)) return false
    return !doc.line_items?.length
  }

  function hasUnallocatedSharedCosts(doc: Document): boolean {
    return (
      doc.has_unallocated_shared_costs === true ||
      (doc.needs_review_flags?.includes('unallocated_shared_costs') ?? false)
    )
  }

  /** Missing vendor/type/location/categories/dates, qbo_duplicate in API flags, etc. */
  function hasBlockingNeedsReviewFlags(doc: Document): boolean {
    return (doc.needs_review_flags ?? []).some((f) => !isBulkWarnOnlyReviewFlag(f))
  }

  /**
   * Blocks bulk send to QBO. Pending duplicate does not block single-doc Approve (detail panel).
   * QBO duplicate (flag or duplicate-check overlay) always blocks bulk. Total mismatch is confirm-only.
   */
  function hasBlockingApproveFlag(doc: Document): boolean {
    return (
      hasUnknownVendor(doc) ||
      hasQboDuplicate(doc) ||
      matchesApprovedReference(doc) ||
      isBillOrVendorCreditWithoutLineItems(doc) ||
      hasUnallocatedSharedCosts(doc) ||
      hasBlockingNeedsReviewFlags(doc)
    )
  }

  /** Mismatch confirm when any total-validation flag exists, or API omitted them but totals disagree. */
  function docNeedsTotalMismatchSendConfirm(doc: Document): boolean {
    const flags = doc.needs_review_flags ?? []
    if (flags.some(isTotalMismatchReviewFlag)) return true
    const flagsWithoutMismatch = flags.filter((f) => !isTotalMismatchReviewFlag(f))
    if (flagsWithoutMismatch.length > 0) return false
    const ext = doc.extracted_total != null ? parseFloat(doc.extracted_total) : NaN
    const tot = doc.total_amount != null ? parseFloat(doc.total_amount) : NaN
    if (!Number.isNaN(ext) && !Number.isNaN(tot) && Math.abs(ext - tot) > 0.009) return true
    if (doc.total_validation_status === 'mismatch') return true
    return false
  }

  const requestSort = useCallback(
    (key: string) => {
      setListPage(1)
      if (listSortKey === key) {
        setListSortDir((d) => (d === 'asc' ? 'desc' : 'asc'))
      } else {
        setListSortKey(key)
        setListSortDir('asc')
      }
    },
    [listSortKey],
  )

  const getSortDirection = useCallback(
    (key: string): 'asc' | 'desc' | null => {
      if (listSortKey !== key) return null
      return listSortDir
    },
    [listSortKey, listSortDir],
  )

  const flagFacetAllows = useCallback(
    (key: string) => {
      if (flagFacets === null) return false
      return flagFacets.includes(key)
    },
    [flagFacets],
  )

  const visibleFlagFilterOptions = useMemo(() => {
    return FLAG_OPTIONS.filter((opt) => flagFacetAllows(opt.value))
  }, [flagFacetAllows])

  const uniqueLocations = useMemo(() => {
    const fromRows = documents.map((d) => d.location).filter(Boolean) as string[]
    return [...new Set([...qboLocationNames, ...fromRows])].sort((a, b) =>
      a.localeCompare(b),
    )
  }, [qboLocationNames, documents])

  const flagGroupContext = useMemo(
    () => ({
      pendingPeerDuplicateIds,
      hasUnknownVendor,
    }),
    [pendingPeerDuplicateIds, qboActiveVendors],
  )

  const documentGroups = useMemo(
    () => buildDocReviewGroups(documents, groupBy, flagGroupContext),
    [documents, groupBy, flagGroupContext],
  )

  const uniqueDisplayDocuments = useMemo(
    () => uniqueDocsInDisplayOrder(documents, documentGroups, groupBy),
    [documents, documentGroups, groupBy],
  )

  const uniqueIndexByDocId = useMemo(() => {
    const map = new Map<number, number>()
    uniqueDisplayDocuments.forEach((doc, index) => {
      map.set(doc.id, index)
    })
    return map
  }, [uniqueDisplayDocuments])

  const uniqueListDocIds = useMemo(() => uniqueDisplayDocuments.map((d) => d.id), [uniqueDisplayDocuments])

  const groupKeysFingerprint = useMemo(
    () => documentGroups.map((group) => group.key).join('\0'),
    [documentGroups],
  )

  useEffect(() => {
    setCollapsedGroupKeys(new Set())
  }, [groupBy, groupKeysFingerprint])

  const isGrouped = groupBy !== 'none'

  const unfilteredTabTotal =
    activeTab === 'pending_review' ? pendingReviewUnfilteredTotal : tabUnfilteredTotal

  const countDisplayTotal = docReviewCountOfTotal(hasActiveFilters, total, unfilteredTabTotal)

  const tableColSpan = activeTab === 'approved' || activeTab === 'deleted' ? 13 : 12

  function toggleGroupCollapsed(groupKey: string) {
    setCollapsedGroupKeys((prev) => {
      const next = new Set(prev)
      if (next.has(groupKey)) {
        next.delete(groupKey)
      } else {
        next.add(groupKey)
      }
      return next
    })
  }

  function armSuppressCheckboxChangeForDocId(docId: number) {
    suppressCheckboxChangeForDocIdRef.current = docId
  }

  const handleRowClick = useCallback(
    (doc: Document, index: number, e: React.MouseEvent) => {
      if (e.shiftKey && selectionAnchorIndexRef.current !== null) {
        const anchor = selectionAnchorIndexRef.current
        const lo = Math.min(anchor, index)
        const hi = Math.max(anchor, index)
        armSuppressCheckboxChangeForDocId(doc.id)
        setFullCohortSelected(false)
        setSelectedIds((prev) => {
          const next = new Set(prev)
          for (const d of uniqueDisplayDocuments.slice(lo, hi + 1)) {
            next.add(d.id)
          }
          return next
        })
      } else if (!e.shiftKey) {
        selectionAnchorIndexRef.current = index
      }
      hydrateDocumentFromListRow(doc)
    },
    [uniqueDisplayDocuments, hydrateDocumentFromListRow]
  )

  /** Shift+click only: prevent native toggle; select range and ignore the following change for this row */
  const handleSelectionCheckboxMouseDown = useCallback(
    (doc: Document, index: number, e: React.MouseEvent<HTMLInputElement>) => {
      e.stopPropagation()
      if (!e.shiftKey || selectionAnchorIndexRef.current === null) {
        return
      }
      e.preventDefault()
      const anchor = selectionAnchorIndexRef.current
      const lo = Math.min(anchor, index)
      const hi = Math.max(anchor, index)
      armSuppressCheckboxChangeForDocId(doc.id)
      setFullCohortSelected(false)
      setSelectedIds((prev) => {
        const next = new Set(prev)
        for (const d of uniqueDisplayDocuments.slice(lo, hi + 1)) {
          next.add(d.id)
        }
        return next
      })
    },
    [uniqueDisplayDocuments]
  )

  const handleSelectionCheckboxChange = useCallback(
    (doc: Document, index: number, e: React.ChangeEvent<HTMLInputElement>) => {
      e.stopPropagation()
      if (suppressCheckboxChangeForDocIdRef.current === doc.id) {
        const suppressedId = doc.id
        setTimeout(() => {
          if (suppressCheckboxChangeForDocIdRef.current === suppressedId) {
            suppressCheckboxChangeForDocIdRef.current = null
          }
        }, 0)
        return
      }
      selectionAnchorIndexRef.current = index
      const checked = e.target.checked
      setFullCohortSelected(false)
      setSelectedIds((prev) => {
        const next = new Set(prev)
        if (checked) {
          next.add(doc.id)
        } else {
          next.delete(doc.id)
        }
        return next
      })
    },
    []
  )

  const selectedDocumentIndex = useMemo(() => {
    if (!selectedDocument) return -1
    return uniqueDisplayDocuments.findIndex((d) => d.id === selectedDocument.id)
  }, [selectedDocument, uniqueDisplayDocuments])

  function handleNavigatePrev() {
    const prevDoc = uniqueDisplayDocuments[selectedDocumentIndex - 1]
    if (selectedDocumentIndex > 0 && prevDoc) {
      hydrateDocumentFromListRow(prevDoc)
    }
  }

  function handleNavigateNext() {
    const nextDoc = uniqueDisplayDocuments[selectedDocumentIndex + 1]
    if (selectedDocumentIndex < uniqueDisplayDocuments.length - 1 && nextDoc) {
      hydrateDocumentFromListRow(nextDoc)
    }
  }

  const allRowsOnPageSelected = useMemo(
    () =>
      uniqueListDocIds.length > 0 &&
      uniqueListDocIds.every((id) => selectedIds.has(id)),
    [uniqueListDocIds, selectedIds],
  )

  const showSelectAllInViewBanner = useMemo(
    () =>
      !isGrouped &&
      !loading &&
      total > listDocuments.length &&
      allRowsOnPageSelected &&
      !fullCohortSelected,
    [isGrouped, loading, total, listDocuments.length, allRowsOnPageSelected, fullCohortSelected],
  )

  const headerSelectAllChecked = useMemo(
    () =>
      fullCohortSelected ||
      (uniqueListDocIds.length > 0 && allRowsOnPageSelected),
    [fullCohortSelected, uniqueListDocIds.length, allRowsOnPageSelected],
  )

  const headerSelectAllIndeterminate = useMemo(
    () =>
      !fullCohortSelected &&
      uniqueListDocIds.some((id) => selectedIds.has(id)) &&
      !allRowsOnPageSelected,
    [fullCohortSelected, uniqueListDocIds, selectedIds, allRowsOnPageSelected],
  )

  useLayoutEffect(() => {
    const el = headerSelectAllCheckboxRef.current
    if (el) {
      el.indeterminate = headerSelectAllIndeterminate
    }
  }, [headerSelectAllIndeterminate])

  const tabs: { id: StatusTab; label: string; count?: number }[] = [
    { id: 'pending_review', label: 'Review' },
    { id: 'approved', label: 'Approved' },
    { id: 'deleted', label: 'Archived' },
  ]

  function formatDate(dateStr: string | null): string {
    return formatCalendarDate(dateStr)
  }

  function formatAmount(amount: string | null): string {
    if (!amount) return '-'
    const num = parseFloat(amount)
    if (num === 0) return '$0.00'
    if (num < 0) return `($${Math.abs(num).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })})`
    return `$${num.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
  }

  function getLineItemTotal(doc: Document): string {
    if (!doc.line_items || doc.line_items.length === 0) {
      return formatAmount(doc.total_amount)
    }
    const sum = doc.line_items.reduce((acc, li) => acc + parseFloat(li.amount || '0'), 0)
    if (sum === 0) return '$0.00'
    if (sum < 0) return `($${Math.abs(sum).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })})`
    return `$${sum.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
  }

  function formatActionAt(iso: string | null): string {
    return formatLocalDateTime(iso)
  }

  function formatReceivedDate(dateStr: string): string {
    return formatLocalDateTime(dateStr)
  }

  /** Local part of inbox address for display, e.g. lauren@thecottagegrocery.com → lauren */
  const { start: pageRangeStart, end: pageRangeEnd } = docReviewPageRange({
    isGrouped,
    listPage,
    pageSize: LIST_PAGE_SIZE,
    filteredTotal: total,
    documentCount: listDocuments.length,
  })

  function renderDocumentRow(doc: Document, uniqueIndex: number, rowKey: string) {
    const docIsDuplicate = isDuplicate(doc)
    const docHasQboDupe = hasQboDuplicate(doc)
    const docHasUnknownVendor = hasUnknownVendor(doc)
    const docMissingVendor =
      (doc.needs_review_flags?.includes('missing_vendor') ?? false) ||
      !doc.vendor_name?.trim()
    const docMissingLineItems = isBillOrVendorCreditWithoutLineItems(doc)
    const docHasNonPayableNotice =
      doc.needs_review_flags?.includes('non_payable_notice') ?? false
    const docMissingLocation = doc.needs_review_flags?.includes('missing_location') ?? false
    const docMissingBillDates =
      doc.needs_review_flags?.includes('missing_bill_dates') ?? false
    const docMissingCategories =
      doc.needs_review_flags?.includes('missing_categories') ?? false
    const docHasUnallocatedSharedCosts = hasUnallocatedSharedCosts(doc)
    const reviewFlagsForAmber = getAmberReviewFlagsFromNeedsReviewFlags(
      doc.needs_review_flags ?? [],
    )
    const hasAmberReviewFlags = reviewFlagsForAmber.length > 0
    const pendingPeerDuplicateOnly = hasPendingPeerDuplicateOnly(doc)
    const docHasAnyFlag =
      docIsDuplicate ||
      docHasQboDupe ||
      docHasUnknownVendor ||
      docMissingVendor ||
      hasAmberReviewFlags ||
      docMissingLineItems ||
      docHasNonPayableNotice ||
      docMissingLocation ||
      docMissingBillDates ||
      docMissingCategories ||
      docHasUnallocatedSharedCosts ||
      pendingPeerDuplicateOnly

    const amberTableWarningTitleParts: string[] = []
    if (pendingPeerDuplicateOnly) {
      amberTableWarningTitleParts.push(
        'Another pending document has the same vendor and reference number',
      )
    }
    if (hasAmberReviewFlags) {
      amberTableWarningTitleParts.push(
        reviewFlagsForAmber.map(formatAmberReviewFlagLine).join(', '),
      )
    }
    const showAmberTableWarningBadge =
      pendingPeerDuplicateOnly || hasAmberReviewFlags

    const redTableBlockingTitleParts: string[] = []
    if (docHasQboDupe) {
      redTableBlockingTitleParts.push(
        'Duplicate in QuickBooks (same vendor and reference)',
      )
    }
    if (matchesApprovedReference(doc)) {
      redTableBlockingTitleParts.push(
        'Same vendor and reference as an already approved document in Bookkeeper',
      )
    }
    if (docMissingLineItems) {
      redTableBlockingTitleParts.push(
        'No line items — add at least one line before sending to QuickBooks',
      )
    }
    if (docMissingLocation) {
      redTableBlockingTitleParts.push(
        'Location is required before sending to QuickBooks',
      )
    }
    if (docMissingBillDates) {
      redTableBlockingTitleParts.push(
        'Bill is missing issue date or due date — both are required before sending to QuickBooks',
      )
    }
    if (docMissingCategories) {
      redTableBlockingTitleParts.push(
        'One or more line items are missing a QuickBooks category',
      )
    }
    if (docHasUnallocatedSharedCosts) {
      redTableBlockingTitleParts.push(
        'Unallocated shared costs — use Allocate shared costs in the document panel before sending to QuickBooks',
      )
    }
    if (docMissingVendor && !docHasUnknownVendor) {
      redTableBlockingTitleParts.push('Missing vendor')
    }
    if (docHasUnknownVendor) {
      redTableBlockingTitleParts.push(
        `Vendor "${doc.vendor_name ?? ''}" not found in QuickBooks`,
      )
    }
    const showRedTableBlockingBadge = redTableBlockingTitleParts.length > 0

    return (
      <tr
        key={rowKey}
        onClick={(e) => handleRowClick(doc, uniqueIndex, e)}
        className={`cursor-pointer ${
          selectedDocument?.id === doc.id ? 'bg-blue-50' : ''
        } ${selectedIds.has(doc.id) ? 'bg-blue-50' : ''}`}
      >
        <td className="max-md:py-2" onClick={(e) => e.stopPropagation()}>
          <input
            type="checkbox"
            checked={selectedIds.has(doc.id)}
            onMouseDown={(e) => handleSelectionCheckboxMouseDown(doc, uniqueIndex, e)}
            onChange={(e) => handleSelectionCheckboxChange(doc, uniqueIndex, e)}
            className="inline-block origin-center scale-110 align-middle rounded border-gray-300"
          />
        </td>
        {activeTab !== 'approved' && (
          <td>
            {docHasAnyFlag && (
              <div className="flex items-center gap-1">
                {showRedTableBlockingBadge && (
                  <span
                    className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded text-xs font-medium bg-red-100 text-red-700"
                    title={redTableBlockingTitleParts.join(' · ')}
                  >
                    <ExclamationTriangleIcon className="w-3 h-3" />
                  </span>
                )}
                {showAmberTableWarningBadge && (
                  <span
                    className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded text-xs font-medium bg-amber-100 text-amber-700"
                    title={amberTableWarningTitleParts.join(' · ')}
                  >
                    <ExclamationTriangleIcon className="w-3 h-3" />
                  </span>
                )}
              </div>
            )}
          </td>
        )}
        <td
          className="max-w-[200px] min-w-[8rem] font-medium truncate sm:max-w-[220px]"
          title={doc.vendor_name || ''}
        >
          {doc.vendor_name || '-'}
        </td>
        <td className="hidden max-md:table-cell sm:table-cell">
          {doc.doc_type ? (
            <span
              className={`badge ${
                doc.doc_type === 'bill' || doc.doc_type === 'card_expense'
                  ? 'badge-info'
                  : 'badge-warning'
              }`}
            >
              {docTypeLabel(doc.doc_type)}
            </span>
          ) : (
            '-'
          )}
        </td>
        <td className="hidden max-md:table-cell min-w-[5rem] text-gray-600 lg:table-cell">
          {doc.location || '-'}
        </td>
        <td
          className="hidden max-md:table-cell max-w-[120px] min-w-[5rem] truncate text-gray-600 md:table-cell"
          title={doc.reference_number || ''}
        >
          {doc.reference_number || '-'}
        </td>
        <td className="hidden max-md:table-cell whitespace-nowrap text-xs text-gray-500 lg:table-cell">
          {formatReceivedDate(doc.created_at)}
        </td>
        <td className="hidden max-md:table-cell text-sm text-gray-600 lg:table-cell">
          {formatDate(doc.issue_date)}
        </td>
        <td className="hidden max-md:table-cell text-sm text-gray-600 xl:table-cell">
          {formatDate(doc.due_date)}
        </td>
        <td className="whitespace-nowrap text-sm font-medium">{getLineItemTotal(doc)}</td>
        <td className="hidden max-md:table-cell xl:table-cell">
          {(() => {
            const source = getSourceDisplay(doc)
            return (
              <span
                className="badge badge-neutral whitespace-nowrap text-xs"
                title={source.title}
              >
                {source.label}
              </span>
            )
          })()}
        </td>
        {activeTab === 'approved' && (
          <td className="hidden max-md:table-cell whitespace-nowrap text-xs text-gray-500 lg:table-cell">
            {formatActionAt(doc.approved_at)}
          </td>
        )}
        {activeTab === 'approved' && (
          <td
            className="hidden max-md:table-cell md:table-cell"
            onClick={(e) => e.stopPropagation()}
          >
            {doc.qbo_entity_id ? (
              <a
                href={qboTxnUrl(doc.qbo_entity_type, doc.qbo_entity_id)}
                target="_blank"
                rel="noopener noreferrer"
                className="text-blue-600 hover:text-blue-800 underline text-sm"
              >
                View
              </a>
            ) : (
              <span className="text-gray-400 text-sm">-</span>
            )}
          </td>
        )}
        {activeTab === 'deleted' && (
          <td className="hidden max-md:table-cell whitespace-nowrap text-xs text-gray-500 lg:table-cell">
            {formatActionAt(doc.deleted_at)}
          </td>
        )}
        <td>
          <div
            className="flex justify-end"
            onClick={(e) => e.stopPropagation()}
          >
            {activeTab === 'pending_review' && canEdit && (
              <button
                onClick={() => handleDelete(doc)}
                className="btn btn-ghost p-1 text-red-600 hover:text-red-700"
                title="Archive"
              >
                <ArchiveBoxIcon className="w-4 h-4" />
              </button>
            )}
            {activeTab === 'approved' && (
              <button
                onClick={() => handleUnapprove(doc)}
                className="btn btn-ghost p-1 text-amber-600 hover:text-amber-700"
                title="Move to Review"
              >
                <ArrowUturnLeftIcon className="w-4 h-4" />
              </button>
            )}
            {activeTab === 'deleted' && (
              <button
                onClick={() => handleRestore(doc)}
                className="btn btn-ghost p-1 text-blue-600 hover:text-blue-700"
                title="Restore"
              >
                <ArrowUturnLeftIcon className="w-4 h-4" />
              </button>
            )}
          </div>
        </td>
      </tr>
    )
  }

  return (
    <div className="flex h-full min-h-0 flex-1 flex-col gap-4 overflow-hidden md:flex-row">
      {/* Main content */}
      <div className="flex min-h-0 flex-1 flex-col overflow-hidden min-w-0">
        {/* Tabs, bulk actions, and filters */}
        <div className="flex-shrink-0 space-y-2 sm:space-y-3 mb-3 sm:mb-4">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2 border-b border-gray-200 pb-2 sm:pb-0">
            <nav className="flex gap-3 sm:gap-6 overflow-x-auto flex-shrink-0">
              {tabs.map((tab) => (
                <button
                  key={tab.id}
                  onClick={() => {
                    setActiveTab(tab.id)
                    setListPage(1)
                    bumpDocumentHydrationGen()
                    setSelectedDocument(null)
                  }}
                  className={`py-2 border-b-2 text-sm font-medium transition-colors whitespace-nowrap ${
                    activeTab === tab.id
                      ? 'border-blue-500 text-blue-600'
                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                  }`}
                >
                  {tab.label}
                </button>
              ))}
            </nav>
            <div className="flex items-center gap-1 flex-wrap">
              <input
                ref={fileInputRef}
                type="file"
                accept=".pdf,.csv,application/pdf,text/csv"
                onChange={handleUpload}
                className="hidden"
                id="pdf-upload"
                multiple
              />

              {/* Review tab actions */}
              {activeTab === 'pending_review' && (
                <>
                  <button
                    onClick={() => fileInputRef.current?.click()}
                    disabled={uploading || processingHintsModal != null}
                    className="btn btn-secondary p-2"
                    title={
                      processingHintsModal
                        ? 'Finish or cancel the upload dialog first'
                        : uploading
                          ? 'Starting upload...'
                          : 'Upload PDFs or CSVs'
                    }
                  >
                    <ArrowUpTrayIcon className={`w-5 h-5 ${uploading ? 'animate-pulse' : ''}`} />
                  </button>
                  <div className="hidden sm:block w-px h-6 bg-gray-300 mx-1" />
                  {selectedIds.size > 0 && (
                    <button
                      onClick={openReprocessHintsModal}
                      disabled={isReprocessing || processingHintsModal != null}
                      className="btn btn-secondary p-2"
                      title={`Re-run extraction on ${selectedIds.size} selected document(s)`}
                    >
                      <SparklesIcon className={`w-5 h-5 ${isReprocessing ? 'animate-pulse' : ''}`} />
                    </button>
                  )}
                  {selectedIds.size > 0 && (
                    <button
                      onClick={() => setBulkMetadataOpen(true)}
                      className="btn btn-secondary p-2"
                      title={`Edit vendor, type, location, or memo for ${selectedIds.size} selected`}
                    >
                      <PencilSquareIcon className="w-5 h-5" />
                    </button>
                  )}
                  <button
                    onClick={handleMatchVendors}
                    disabled={isMatchingVendors || documents.length === 0}
                    className="hidden sm:inline-flex btn btn-secondary p-2"
                    title="Match vendor names to QBO vendors"
                    aria-busy={isMatchingVendors}
                  >
                    {isMatchingVendors ? (
                      <ArrowPathIcon className="w-5 h-5 text-gray-600 animate-spin" aria-hidden />
                    ) : (
                      <UserGroupIcon className="w-5 h-5" aria-hidden />
                    )}
                  </button>
                  {selectedIds.size > 0 && (
                    <button
                      onClick={() => void handleCheckDuplicates()}
                      disabled={isCheckingDuplicates}
                      className="hidden sm:inline-flex btn btn-secondary p-2"
                      title={`Check selected document(s) for duplicate bills/credits in QuickBooks (uses vendor + reference)`}
                      aria-busy={isCheckingDuplicates}
                    >
                      {isCheckingDuplicates ? (
                        <ArrowPathIcon className="w-5 h-5 text-gray-600 animate-spin" aria-hidden />
                      ) : (
                        <DocumentDuplicateIcon className="w-5 h-5" aria-hidden />
                      )}
                    </button>
                  )}
                  <button
                    onClick={handleRefreshAllCategories}
                    disabled={
                      isStartingRefreshCategories ||
                      (selectedIds.size === 0 && documents.length === 0)
                    }
                    className="hidden sm:inline-flex btn btn-secondary p-2"
                    title={
                      selectedIds.size > 0
                        ? `Re-infer expense categories from line items for ${selectedIds.size} selected document(s)`
                        : 'Re-infer expense categories for all documents on this page (select rows to run on a chosen set)'
                    }
                    aria-busy={isStartingRefreshCategories}
                  >
                    {isStartingRefreshCategories ? (
                      <ArrowPathIcon className="w-5 h-5 text-gray-600 animate-spin" aria-hidden />
                    ) : (
                      <Squares2X2Icon className="w-5 h-5" aria-hidden />
                    )}
                  </button>
                  {selectedIds.size > 0 && (
                    <>
                      <div className="hidden sm:block w-px h-6 bg-gray-300 mx-1" />
                      <button
                        onClick={handleBulkApprove}
                        disabled={!canEdit || isBulkApproving || getEligibleSelectedCount() === 0}
                        className="btn btn-secondary p-2"
                        title={getEligibleSelectedCount() === 0 
                          ? "No selected documents can be sent — resolve blocking flags first"
                          : `Approve ${getEligibleSelectedCount()} selected and send to QBO`}
                      >
                        <CheckCircleIcon className={`w-5 h-5 ${isBulkApproving ? 'animate-pulse' : ''}`} />
                      </button>
                      <button
                        onClick={handleBulkArchive}
                        disabled={!canEdit}
                        className="btn btn-secondary p-2"
                        title={`Archive ${selectedIds.size} selected`}
                      >
                        <ArchiveBoxIcon className="w-5 h-5" />
                      </button>
                    </>
                  )}
                </>
              )}

              {/* Approved tab actions */}
              {activeTab === 'approved' && selectedIds.size > 0 && (
                <button
                  onClick={handleBulkRestore}
                  className="btn btn-secondary p-2"
                  title={`Restore ${selectedIds.size} selected to Review`}
                >
                  <ArrowUturnLeftIcon className="w-5 h-5" />
                </button>
              )}

              {/* Archived tab actions */}
              {activeTab === 'deleted' && selectedIds.size > 0 && (
                <>
                  <button
                    onClick={handleBulkRestore}
                    className="btn btn-secondary p-2"
                    title={`Restore ${selectedIds.size} selected to Review`}
                  >
                    <ArrowUturnLeftIcon className="w-5 h-5" />
                  </button>
                  <button
                    onClick={() => handlePermanentDelete(Array.from(selectedIds))}
                    className="btn btn-danger p-2"
                    title={`Permanently delete ${selectedIds.size} selected`}
                  >
                    <TrashIcon className="w-5 h-5" />
                  </button>
                </>
              )}

              {/* Selection count badge */}
              {selectedIds.size > 0 && (
                <span className="ml-1 sm:ml-2 text-xs sm:text-sm text-gray-500 whitespace-nowrap">
                  {selectedIds.size} sel
                </span>
              )}
            </div>
          </div>

          {/* Filter toggle button - visible on mobile */}
          <button
            onClick={() => setShowFilters(!showFilters)}
            className="md:hidden flex items-center gap-2 w-full p-2 bg-gray-50 rounded-lg border border-gray-200 text-sm text-gray-700"
          >
            <FunnelIcon className="w-4 h-4" />
            <span className="flex-1 text-left">
              Filters
              {hasActiveFilters && (
                <span className="ml-2 px-1.5 py-0.5 text-xs bg-gray-100 text-gray-700 rounded-full">
                  Active
                </span>
              )}
            </span>
            {showFilters ? (
              <ChevronUpIcon className="w-4 h-4" />
            ) : (
              <ChevronDownIcon className="w-4 h-4" />
            )}
          </button>

          {/* Filter bar - always visible on desktop, toggleable on mobile */}
          <div className={`${showFilters ? 'block' : 'hidden'} md:block`}>
            <div className="filter-bar">
              <div className="flex flex-col gap-1">
                <label htmlFor="filter-vendor" className={`text-xs font-medium ${filters.vendor ? 'text-gray-900' : 'text-gray-600'}`}>Vendor</label>
                <input
                  id="filter-vendor"
                  type="text"
                  placeholder="Search..."
                  className={getFilterInputClass(filters.vendor)}
                  value={filters.vendor}
                  onChange={(e) => handleFilterChange('vendor', e.target.value)}
                />
              </div>
              <div className="flex flex-col gap-1">
                <label htmlFor="filter-doc-type" className={`text-xs font-medium ${filters.doc_type ? 'text-gray-900' : 'text-gray-600'}`}>Type</label>
                <select
                  id="filter-doc-type"
                  className={getFilterSelectClass(filters.doc_type)}
                  value={filters.doc_type}
                  onChange={(e) => handleFilterChange('doc_type', e.target.value)}
                >
                  <option value="">All</option>
                  <option value="bill">Bill</option>
                  <option value="vendor_credit">Credit</option>
                </select>
              </div>
              <div className="flex flex-col gap-1">
                <label htmlFor="filter-location" className={`text-xs font-medium ${filters.location ? 'text-gray-900' : 'text-gray-600'}`}>Location</label>
                <select
                  id="filter-location"
                  className={getFilterSelectClass(filters.location)}
                  value={filters.location}
                  onChange={(e) => handleFilterChange('location', e.target.value)}
                >
                  <option value="">All</option>
                  {uniqueLocations.map((loc) => (
                    <option key={loc} value={loc!}>{loc}</option>
                  ))}
                </select>
              </div>
              <div className="flex flex-col gap-1">
                <label htmlFor="filter-ref" className={`text-xs font-medium ${filters.reference_number ? 'text-gray-900' : 'text-gray-600'}`}>Doc Ref #</label>
                <input
                  id="filter-ref"
                  type="text"
                  placeholder="Search..."
                  className={getFilterInputClass(filters.reference_number)}
                  value={filters.reference_number}
                  onChange={(e) => handleFilterChange('reference_number', e.target.value)}
                />
              </div>
              <div className="flex flex-col gap-1">
                <label
                  htmlFor="filter-date-from"
                  className={`text-xs font-medium ${filters.date_from ? 'text-gray-900' : 'text-gray-600'}`}
                  title="Filters by document issue date (same as the Issue column)"
                >
                  From (Issue Date)
                </label>
                <DateInput
                  className={getFilterInputClass(filters.date_from)}
                  value={filters.date_from}
                  onChange={(value) => handleFilterChange('date_from', value)}
                  dateFormat="iso"
                />
              </div>
              <div className="flex flex-col gap-1">
                <label
                  htmlFor="filter-date-to"
                  className={`text-xs font-medium ${filters.date_to ? 'text-gray-900' : 'text-gray-600'}`}
                  title="Filters by document issue date (same as the Issue column)"
                >
                  To (Issue Date)
                </label>
                <DateInput
                  className={getFilterInputClass(filters.date_to)}
                  value={filters.date_to}
                  onChange={(value) => handleFilterChange('date_to', value)}
                  dateFormat="iso"
                />
              </div>
              <div className="filter-bar-amount flex flex-col gap-1">
                <label
                  htmlFor="filter-amount-value"
                  className={`text-xs font-medium ${hasActiveAmountFilter ? 'text-gray-900' : 'text-gray-600'}`}
                  title="Filters by the Amount column (sum of line items, or document total when there are no lines)"
                >
                  Amount
                </label>
                <div className="filter-bar-amount-controls">
                  <select
                    id="filter-amount-op"
                    aria-label="Amount comparison"
                    className={
                      hasActiveAmountFilter
                        ? 'filter-bar-amount-op select ring-2 ring-gray-300 bg-gray-50'
                        : 'filter-bar-amount-op select'
                    }
                    value={filters.amount_op}
                    onChange={(e) =>
                      handleFilterChange('amount_op', e.target.value as AmountFilterOp)
                    }
                  >
                    {AMOUNT_FILTER_OPS.map((opt) => (
                      <option key={opt.value} value={opt.value}>
                        {opt.label}
                      </option>
                    ))}
                  </select>
                  <input
                    id="filter-amount-value"
                    type="text"
                    inputMode="decimal"
                    placeholder="0.00"
                    className={`filter-bar-amount-value ${getFilterInputClass(filters.amount)}`}
                    value={filters.amount}
                    onChange={(e) => handleFilterChange('amount', e.target.value)}
                  />
                </div>
              </div>
              <div className="flex flex-col gap-1">
                <label htmlFor="filter-source" className={`text-xs font-medium ${filters.source_type ? 'text-gray-900' : 'text-gray-600'}`}>Source</label>
                <select
                  id="filter-source"
                  className={getFilterSelectClass(filters.source_type)}
                  value={filters.source_type}
                  onChange={(e) => handleFilterChange('source_type', e.target.value)}
                >
                  <option value="">All</option>
                  {SOURCE_FILTER_OPTIONS.map((opt) => (
                    <option key={opt.value} value={opt.value}>{opt.label}</option>
                  ))}
                </select>
              </div>
              <div className="flex flex-col gap-1">
                <label htmlFor="filter-flags" className={`text-xs font-medium ${filters.flags ? 'text-gray-900' : 'text-gray-600'}`}>Flags</label>
                <select
                  id="filter-flags"
                  className={getFilterSelectClass(filters.flags)}
                  value={filters.flags}
                  onChange={(e) => handleFilterChange('flags', e.target.value)}
                >
                  <option value="">All</option>
                  {visibleFlagFilterOptions.map((opt) => (
                    <option key={opt.value} value={opt.value}>{opt.label}</option>
                  ))}
                  {flagFacetAllows('any_flag') && (
                    <option value="any_flag">Any flag</option>
                  )}
                  {flagFacetAllows('no_flags') && (
                    <option value="no_flags">No flags</option>
                  )}
                </select>
              </div>
              {hasActiveFilters && (
                <button onClick={clearFilters} className="btn btn-secondary text-sm px-3 py-1.5 self-end">
                  Clear
                </button>
              )}
            </div>
          </div>
        </div>

        {/* Table */}
        <div
          ref={dropZoneRef}
          className="relative flex min-h-0 flex-1 flex-col overflow-hidden"
          onDragEnter={handleDragEnter}
          onDragLeave={handleDragLeave}
          onDragOver={handleDragOver}
          onDrop={handleDrop}
        >
          {/* Drag-and-drop overlay */}
          {isDraggingOver && !processingHintsModal && (
            <div className="absolute inset-0 z-10 bg-blue-50/90 border-2 border-dashed border-blue-400 rounded-lg flex flex-col items-center justify-center pointer-events-none">
              <ArrowUpTrayIcon className="w-12 h-12 text-blue-500 mb-3" />
              <p className="text-lg font-medium text-blue-700">Drop PDFs or CSVs here to upload</p>
              <p className="text-sm text-blue-500 mt-1">Multiple files supported</p>
            </div>
          )}
          <TablePageShell
            className="doc-review-table-shell min-h-0 flex-1"
            rowCount={
              <div className="mb-2 flex items-end justify-between gap-3">
                <div className="min-w-0 text-xs text-gray-500 sm:text-sm truncate">
                  Showing {pageRangeStart}-{pageRangeEnd} of {countDisplayTotal}
                  {docReviewCountDocSuffix(countDisplayTotal, hasActiveFilters)}
                  {docReviewCountFilteredSuffix(hasActiveFilters)} —{' '}
                  {activeTab.replace('_', ' ')}
                  {selectedIds.size > 0 ? (
                    <span className="ml-1">· {selectedIds.size} selected</span>
                  ) : null}
                </div>
                <label className="flex shrink-0 items-center gap-2">
                  <span className="text-xs font-medium text-gray-600 whitespace-nowrap">
                    Group by
                  </span>
                  <GroupBySelect
                    value={groupBy}
                    onChange={(value) => setGroupBy(value as DocReviewGroupBy)}
                    options={DOC_REVIEW_GROUP_OPTIONS}
                    aria-label="Group by"
                    className="shrink-0"
                  />
                </label>
              </div>
            }
            filterPills={
              filterPills.length > 0 ? (
                <ActiveFilterPillBar
                  pills={filterPills}
                  onRemovePill={handleRemoveFilterPill}
                  onClearAll={clearFilters}
                />
              ) : null
            }
          >
            {loading ? (
              <div className="flex h-full min-h-0 items-center justify-center">
                <div className="text-gray-500">Loading documents...</div>
              </div>
            ) : (
              <div className="flex h-full min-h-0 flex-1 flex-col overflow-hidden">
                {showSelectAllInViewBanner && (
                  <div className="flex-shrink-0 flex flex-col sm:flex-row sm:items-center sm:flex-wrap gap-1 sm:gap-2 px-3 py-2 border-b border-blue-100 bg-blue-50 text-sm text-blue-900">
                    <span>
                      All {listDocuments.length}{' '}
                      {listDocuments.length === 1 ? 'row' : 'rows'} on this page are selected.
                    </span>
                    <button
                      type="button"
                      disabled={isSelectingAllInView}
                      className="font-semibold text-blue-700 hover:text-blue-900 underline disabled:opacity-50 disabled:no-underline text-left"
                      onClick={() => void handleSelectAllInView()}
                    >
                      {isSelectingAllInView
                        ? 'Selecting…'
                        : `Select all ${total} in this view`}
                    </button>
                  </div>
                )}
                <div className="table-scroll-mobile min-h-0 flex-1 overflow-auto overscroll-x-contain">
              <table className="table-forecast doc-review-table w-full min-w-max border-separate border-spacing-0">
                <thead>
                  <tr>
                    <th className="w-10 max-md:min-w-[44px] max-md:py-2">
                      <input
                        ref={headerSelectAllCheckboxRef}
                        type="checkbox"
                        checked={headerSelectAllChecked}
                        onChange={toggleSelectAll}
                        className="inline-block origin-center scale-110 align-middle rounded border-gray-300"
                      />
                    </th>
                    {activeTab !== 'approved' && <th className="w-12 sm:w-16">Flags</th>}
                    <SortableHeader
                      label="Vendor"
                      sortKey="vendor_name"
                      sortDirection={getSortDirection('vendor_name')}
                      onSort={requestSort}
                    />
                    <SortableHeader
                      label="Type"
                      sortKey="doc_type"
                      sortDirection={getSortDirection('doc_type')}
                      onSort={requestSort}
                      className="hidden max-md:table-cell sm:table-cell"
                    />
                    <SortableHeader
                      label="Location"
                      sortKey="location"
                      sortDirection={getSortDirection('location')}
                      onSort={requestSort}
                      className="hidden max-md:table-cell lg:table-cell"
                    />
                    <SortableHeader
                      label="Doc Ref #"
                      sortKey="reference_number"
                      sortDirection={getSortDirection('reference_number')}
                      onSort={requestSort}
                      className="hidden max-md:table-cell md:table-cell"
                    />
                    <SortableHeader
                      label="Received"
                      sortKey="created_at"
                      sortDirection={getSortDirection('created_at')}
                      onSort={requestSort}
                      className="hidden max-md:table-cell lg:table-cell"
                    />
                    <SortableHeader
                      label="Issue Date"
                      sortKey="issue_date"
                      sortDirection={getSortDirection('issue_date')}
                      onSort={requestSort}
                      className="hidden max-md:table-cell lg:table-cell"
                    />
                    <SortableHeader
                      label="Due Date"
                      sortKey="due_date"
                      sortDirection={getSortDirection('due_date')}
                      onSort={requestSort}
                      className="hidden max-md:table-cell xl:table-cell"
                    />
                    <SortableHeader
                      label="Amount"
                      sortKey="amount"
                      sortDirection={getSortDirection('amount')}
                      onSort={requestSort}
                    />
                    <th className="hidden max-md:table-cell xl:table-cell w-auto whitespace-nowrap">
                      Source
                    </th>
                    {activeTab === 'approved' && (
                      <SortableHeader
                        label="Approved at"
                        sortKey="approved_at"
                        sortDirection={getSortDirection('approved_at')}
                        onSort={requestSort}
                        className="hidden max-md:table-cell lg:table-cell whitespace-nowrap"
                      />
                    )}
                    {activeTab === 'approved' && (
                      <th className="hidden max-md:table-cell md:table-cell w-auto whitespace-nowrap">QBO</th>
                    )}
                    {activeTab === 'deleted' && (
                      <SortableHeader
                        label="Archived at"
                        sortKey="deleted_at"
                        sortDirection={getSortDirection('deleted_at')}
                        onSort={requestSort}
                        className="hidden max-md:table-cell lg:table-cell whitespace-nowrap"
                      />
                    )}
                    <th className="w-10 sm:w-12"></th>
                  </tr>
                </thead>
                <tbody>
                  {listDocuments.length === 0 ? (
                    <tr>
                      <td
                        colSpan={tableColSpan}
                        className="text-center text-gray-500 py-8"
                      >
                        No documents found.
                      </td>
                    </tr>
                  ) : isGrouped ? (
                    documentGroups.map((group) => {
                      const collapsed = collapsedGroupKeys.has(group.key)
                      return (
                        <Fragment key={group.key}>
                          <tr
                            className="group-header cursor-pointer"
                            onClick={(e) => {
                              e.stopPropagation()
                              toggleGroupCollapsed(group.key)
                            }}
                          >
                            <td colSpan={tableColSpan} className="font-medium text-sm text-gray-700">
                              <span className="inline-flex items-center gap-1.5">
                                {collapsed ? (
                                  <ChevronDownIcon className="h-4 w-4 shrink-0 text-gray-500" />
                                ) : (
                                  <ChevronUpIcon className="h-4 w-4 shrink-0 text-gray-500" />
                                )}
                                <span>{group.label}</span>
                                <span className="font-normal text-gray-500">({group.documents.length})</span>
                              </span>
                            </td>
                          </tr>
                          {!collapsed &&
                            group.documents.map((doc) =>
                              renderDocumentRow(
                                doc,
                                uniqueIndexByDocId.get(doc.id) ?? 0,
                                `${group.key}-${doc.id}`,
                              ),
                            )}
                        </Fragment>
                      )
                    })
                  ) : (
                    listDocuments.map((doc, index) =>
                      renderDocumentRow(doc, index, String(doc.id)),
                    )
                  )}
                </tbody>
              </table>
            </div>
              {total > 0 && !isGrouped && (
                <div className="flex flex-shrink-0 flex-col gap-2 border-t border-gray-100 bg-gray-50 px-3 py-2 text-sm sm:flex-row sm:items-center sm:justify-between">
                  <span className="text-gray-600 tabular-nums">
                    {`Page ${listPage} — rows ${pageRangeStart}-${pageRangeEnd} of ${total}`}
                  </span>
                  <div className="flex gap-2">
                    <button
                      type="button"
                      className="btn btn-secondary text-sm px-3 py-1"
                      disabled={listPage <= 1 || loading}
                      onClick={() => setListPage((p) => Math.max(1, p - 1))}
                    >
                      Previous
                    </button>
                    <button
                      type="button"
                      className="btn btn-secondary text-sm px-3 py-1"
                      disabled={listPage * LIST_PAGE_SIZE >= total || loading}
                      onClick={() => setListPage((p) => p + 1)}
                    >
                      Next
                    </button>
                  </div>
                </div>
              )}
              </div>
            )}
          </TablePageShell>
        </div>

      </div>

      {/* Side panel */}
      {selectedDocument && (
        <DocumentPanel
          document={selectedDocument}
          previewHydrating={documentPreviewHydrating}
          onClose={() => {
            bumpDocumentHydrationGen()
            setSelectedDocument(null)
            setApprovalError(null)
            setOverwriteError(null)
          }}
          onUpdate={handleDocumentUpdate}
          onVendorCreated={handleQboVendorCreated}
          onApprove={(opts) => handleApprove(selectedDocument, opts)}
          onDelete={() => handleDelete(selectedDocument)}
          onUnapprove={activeTab === 'approved' ? () => handleUnapprove(selectedDocument) : undefined}
          onRestore={activeTab === 'deleted' ? () => handleRestore(selectedDocument) : undefined}
          onNavigatePrev={() => {
            setApprovalError(null)
            setOverwriteError(null)
            handleNavigatePrev()
          }}
          onNavigateNext={() => {
            setApprovalError(null)
            setOverwriteError(null)
            handleNavigateNext()
          }}
          hasPrev={selectedDocumentIndex > 0}
          hasNext={selectedDocumentIndex < uniqueDisplayDocuments.length - 1}
          isPendingDuplicate={
            selectedDocument.duplicate_peer_pending === true ||
            pendingPeerDuplicateIds.has(selectedDocument.id)
          }
          duplicateMatchesApproved={selectedDocument.duplicate_matches_approved === true}
          duplicateApprovedDocumentId={selectedDocument.duplicate_approved_document_id ?? null}
          duplicateApprovedQboEntityId={selectedDocument.duplicate_approved_qbo_entity_id ?? null}
          duplicateApprovedQboEntityType={selectedDocument.duplicate_approved_qbo_entity_type ?? null}
          duplicateApprovedLineItemCount={selectedDocument.duplicate_approved_line_item_count ?? null}
          duplicateApprovedTotalAmount={selectedDocument.duplicate_approved_total_amount ?? null}
          onOverwriteApprovedDuplicate={
            activeTab === 'pending_review'
              ? () => handleOverwriteApprovedDuplicate(selectedDocument)
              : undefined
          }
          isOverwriting={isOverwriting}
          overwriteError={overwriteError}
          onClearOverwriteError={() => setOverwriteError(null)}
          isApproving={isApproving}
          approvalError={approvalError}
          onClearApprovalError={() => setApprovalError(null)}
          canEdit={canEdit}
        />
      )}

      {/* Bulk metadata edit modal (Review tab) */}
      {bulkMetadataOpen && (
        <div
          className="fixed inset-0 z-50 flex items-end justify-center sm:items-center bg-slate-900/40 backdrop-blur-[2px] p-0 sm:p-4"
          role="dialog"
          aria-modal="true"
          aria-labelledby="bulk-metadata-title"
        >
          <div className="bg-white rounded-t-2xl sm:rounded-2xl shadow-2xl ring-1 ring-black/5 max-w-lg w-full max-h-[92vh] sm:max-h-[min(85vh,720px)] flex flex-col">
            <div className="flex-shrink-0 px-5 pt-5 pb-4 border-b border-gray-100">
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <h3
                      id="bulk-metadata-title"
                      className="text-lg font-semibold text-gray-900 tracking-tight"
                    >
                      Bulk edit metadata
                    </h3>
                    <span className="inline-flex items-center rounded-full bg-slate-100 px-2.5 py-0.5 text-xs font-semibold tabular-nums text-slate-700">
                      {selectedIds.size} doc{selectedIds.size === 1 ? '' : 's'}
                    </span>
                  </div>
                  <p className="mt-1 text-sm text-gray-500 leading-snug">
                    Turn on the fields you want to overwrite. Everything else stays as-is on each
                    document.
                  </p>
                </div>
                <button
                  type="button"
                  onClick={() => !bulkMetadataModalBusy && setBulkMetadataOpen(false)}
                  className="flex-shrink-0 rounded-lg p-1.5 text-gray-400 transition-colors hover:bg-gray-100 hover:text-gray-600 disabled:pointer-events-none disabled:opacity-40"
                  disabled={bulkMetadataModalBusy}
                  aria-label="Close"
                >
                  <XMarkIcon className="w-5 h-5" />
                </button>
              </div>
            </div>

            <div className="relative flex-1 min-h-0 overflow-y-auto px-5 py-4">
              {bulkMetaQboLoading && (
                <div className="absolute inset-0 z-10 flex flex-col items-center justify-center gap-3 rounded-b-2xl bg-white/85 backdrop-blur-sm">
                  <ArrowPathIcon className="h-8 w-8 text-blue-600 animate-spin" aria-hidden />
                  <p className="text-sm font-medium text-gray-700">Loading QuickBooks data</p>
                  <p className="text-xs text-gray-500">Vendors, locations, categories, and terms</p>
                </div>
              )}

              <div className="space-y-3">
                {/* Vendor */}
                <div
                  className={`rounded-xl border p-4 transition-colors ${
                    bulkMetaApplyVendor
                      ? 'border-blue-200/80 bg-blue-50/50 shadow-sm shadow-blue-900/5'
                      : 'border-gray-200/90 bg-gray-50/40'
                  }`}
                >
                  <div className="flex gap-3">
                    <input
                      id="bulk-meta-vendor"
                      type="checkbox"
                      className="mt-0.5 h-4 w-4 shrink-0 rounded border-gray-300 text-blue-600 focus:ring-blue-500 focus:ring-offset-0"
                      checked={bulkMetaApplyVendor}
                      onChange={(e) => setBulkMetaApplyVendor(e.target.checked)}
                      disabled={bulkMetaQboLoading || bulkMetadataModalBusy}
                    />
                    <div className="min-w-0 flex-1 space-y-2">
                      <div className="flex flex-wrap items-baseline justify-between gap-x-2 gap-y-0.5">
                        <label
                          htmlFor="bulk-meta-vendor"
                          className="text-sm font-semibold text-gray-900 cursor-pointer"
                        >
                          Vendor
                        </label>
                        {bulkMetaApplyVendor && (
                          <span className="text-[10px] font-semibold uppercase tracking-wider text-blue-600/90">
                            Included
                          </span>
                        )}
                      </div>
                      <p className="text-xs text-gray-500 leading-relaxed">
                        Sets the same QBO vendor on all selected documents.
                      </p>
                      <SearchableSelect
                        options={bulkMetaVendorOptions}
                        value={bulkMetaVendorName}
                        onChange={(_, label) => setBulkMetaVendorName(label)}
                        placeholder="Search or create vendor…"
                        disabled={!bulkMetaApplyVendor || bulkMetaQboLoading || bulkMetadataModalBusy}
                        allowCreate
                        onCreateNew={handleBulkCreateVendor}
                        createLabel="Create vendor"
                        useViewportForPlacement
                        preferBelow
                      />
                    </div>
                  </div>
                </div>

                {/* Document type */}
                <div
                  className={`rounded-xl border p-4 transition-colors ${
                    bulkMetaApplyDocType
                      ? 'border-blue-200/80 bg-blue-50/50 shadow-sm shadow-blue-900/5'
                      : 'border-gray-200/90 bg-gray-50/40'
                  }`}
                >
                  <div className="flex gap-3">
                    <input
                      id="bulk-meta-doctype"
                      type="checkbox"
                      className="mt-0.5 h-4 w-4 shrink-0 rounded border-gray-300 text-blue-600 focus:ring-blue-500 focus:ring-offset-0"
                      checked={bulkMetaApplyDocType}
                      onChange={(e) => setBulkMetaApplyDocType(e.target.checked)}
                      disabled={bulkMetadataModalBusy}
                    />
                    <div className="min-w-0 flex-1 space-y-2">
                      <div className="flex flex-wrap items-baseline justify-between gap-x-2 gap-y-0.5">
                        <label
                          htmlFor="bulk-meta-doctype"
                          className="text-sm font-semibold text-gray-900 cursor-pointer"
                        >
                          Document type
                        </label>
                        {bulkMetaApplyDocType && (
                          <span className="text-[10px] font-semibold uppercase tracking-wider text-blue-600/90">
                            Included
                          </span>
                        )}
                      </div>
                      <p className="text-xs text-gray-500 leading-relaxed">
                        Bill or vendor credit for every selection.
                      </p>
                      <div
                        className={`flex rounded-lg bg-gray-100/90 p-0.5 gap-0.5 ${!bulkMetaApplyDocType || bulkMetadataModalBusy ? 'opacity-50 pointer-events-none' : ''}`}
                        role="group"
                        aria-label="Document type"
                      >
                        <button
                          type="button"
                          disabled={!bulkMetaApplyDocType || bulkMetadataModalBusy}
                          onClick={() => setBulkMetaDocType('bill')}
                          className={`flex-1 rounded-md py-2 px-2 text-xs font-semibold transition-all ${
                            bulkMetaDocType === 'bill'
                              ? 'bg-white text-gray-900 shadow-sm ring-1 ring-black/5'
                              : 'text-gray-500 hover:text-gray-700'
                          }`}
                        >
                          Bill
                        </button>
                        <button
                          type="button"
                          disabled={!bulkMetaApplyDocType || bulkMetadataModalBusy}
                          onClick={() => setBulkMetaDocType('vendor_credit')}
                          className={`flex-1 rounded-md py-2 px-2 text-xs font-semibold transition-all ${
                            bulkMetaDocType === 'vendor_credit'
                              ? 'bg-white text-gray-900 shadow-sm ring-1 ring-black/5'
                              : 'text-gray-500 hover:text-gray-700'
                          }`}
                        >
                          Vendor credit
                        </button>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Location */}
                <div
                  className={`rounded-xl border p-4 transition-colors ${
                    bulkMetaApplyLocation
                      ? 'border-blue-200/80 bg-blue-50/50 shadow-sm shadow-blue-900/5'
                      : 'border-gray-200/90 bg-gray-50/40'
                  }`}
                >
                  <div className="flex gap-3">
                    <input
                      id="bulk-meta-location"
                      type="checkbox"
                      className="mt-0.5 h-4 w-4 shrink-0 rounded border-gray-300 text-blue-600 focus:ring-blue-500 focus:ring-offset-0"
                      checked={bulkMetaApplyLocation}
                      onChange={(e) => setBulkMetaApplyLocation(e.target.checked)}
                      disabled={bulkMetaQboLoading || bulkMetadataModalBusy}
                    />
                    <div className="min-w-0 flex-1 space-y-2">
                      <div className="flex flex-wrap items-baseline justify-between gap-x-2 gap-y-0.5">
                        <label
                          htmlFor="bulk-meta-location"
                          className="text-sm font-semibold text-gray-900 cursor-pointer"
                        >
                          Location
                        </label>
                        {bulkMetaApplyLocation && (
                          <span className="text-[10px] font-semibold uppercase tracking-wider text-blue-600/90">
                            Included
                          </span>
                        )}
                      </div>
                      <p className="text-xs text-gray-500 leading-relaxed">
                        Class / location sent to QuickBooks with each document.
                      </p>
                      <select
                        className="input select w-full text-sm"
                        value={bulkMetaLocationId}
                        onChange={(e) => setBulkMetaLocationId(e.target.value)}
                        disabled={!bulkMetaApplyLocation || bulkMetaQboLoading || bulkMetadataModalBusy}
                        aria-label="Location"
                      >
                        <option value="">Choose location…</option>
                        {bulkMetaLocations.map((loc) => (
                          <option key={loc.id} value={loc.id}>
                            {loc.name}
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>
                </div>

                {/* Payment terms (same QBO options as Document Details) */}
                <div
                  className={`rounded-xl border p-4 transition-colors ${
                    bulkMetaApplyTerms
                      ? 'border-blue-200/80 bg-blue-50/50 shadow-sm shadow-blue-900/5'
                      : 'border-gray-200/90 bg-gray-50/40'
                  }`}
                >
                  <div className="flex gap-3">
                    <input
                      id="bulk-meta-terms"
                      type="checkbox"
                      className="mt-0.5 h-4 w-4 shrink-0 rounded border-gray-300 text-blue-600 focus:ring-blue-500 focus:ring-offset-0"
                      checked={bulkMetaApplyTerms}
                      onChange={(e) => setBulkMetaApplyTerms(e.target.checked)}
                      disabled={bulkMetaQboLoading || bulkMetadataModalBusy}
                    />
                    <div className="min-w-0 flex-1 space-y-2">
                      <div className="flex flex-wrap items-baseline justify-between gap-x-2 gap-y-0.5">
                        <label
                          htmlFor="bulk-meta-terms"
                          className="text-sm font-semibold text-gray-900 cursor-pointer"
                        >
                          Terms
                        </label>
                        {bulkMetaApplyTerms && (
                          <span className="text-[10px] font-semibold uppercase tracking-wider text-blue-600/90">
                            Included
                          </span>
                        )}
                      </div>
                      <p className="text-xs text-gray-500 leading-relaxed">
                        QuickBooks payment terms stored on each document (same list as in Document
                        Details). For bills that already have an issue date, the due date is
                        recalculated from the term (same as changing terms in Document Details).
                        Choose None to clear terms everywhere.
                      </p>
                      <select
                        className="input select w-full text-sm"
                        value={bulkMetaTermId}
                        onChange={(e) => setBulkMetaTermId(e.target.value)}
                        disabled={!bulkMetaApplyTerms || bulkMetaQboLoading || bulkMetadataModalBusy}
                        aria-label="Payment terms"
                      >
                        <option value="">None</option>
                        {bulkMetaTerms.map((t) => (
                          <option key={t.id} value={t.id}>
                            {t.name}
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>
                </div>

                {/* Line item category (all lines on each doc) */}
                <div
                  className={`rounded-xl border p-4 transition-colors ${
                    bulkMetaApplyLineCategory
                      ? 'border-blue-200/80 bg-blue-50/50 shadow-sm shadow-blue-900/5'
                      : 'border-gray-200/90 bg-gray-50/40'
                  }`}
                >
                  <div className="flex gap-3">
                    <input
                      id="bulk-meta-line-category"
                      type="checkbox"
                      className="mt-0.5 h-4 w-4 shrink-0 rounded border-gray-300 text-blue-600 focus:ring-blue-500 focus:ring-offset-0"
                      checked={bulkMetaApplyLineCategory}
                      onChange={(e) => setBulkMetaApplyLineCategory(e.target.checked)}
                      disabled={bulkMetaQboLoading || bulkMetadataModalBusy}
                    />
                    <div className="min-w-0 flex-1 space-y-2">
                      <div className="flex flex-wrap items-baseline justify-between gap-x-2 gap-y-0.5">
                        <label
                          htmlFor="bulk-meta-line-category"
                          className="text-sm font-semibold text-gray-900 cursor-pointer"
                        >
                          Line item category
                        </label>
                        {bulkMetaApplyLineCategory && (
                          <span className="text-[10px] font-semibold uppercase tracking-wider text-blue-600/90">
                            Included
                          </span>
                        )}
                      </div>
                      <p className="text-xs text-gray-500 leading-relaxed">
                        Sets the same QuickBooks expense category on every line item for each selected
                        document.
                      </p>
                      <SearchableSelect
                        options={bulkMetaCategoryOptions}
                        value={bulkMetaCategoryId}
                        onChange={(id) => setBulkMetaCategoryId(id)}
                        placeholder="Search categories…"
                        disabled={
                          !bulkMetaApplyLineCategory || bulkMetaQboLoading || bulkMetadataModalBusy
                        }
                        useViewportForPlacement
                        preferBelow
                      />
                    </div>
                  </div>
                </div>

                {/* Memo */}
                <div
                  className={`rounded-xl border p-4 transition-colors ${
                    bulkMetaApplyMemo
                      ? 'border-blue-200/80 bg-blue-50/50 shadow-sm shadow-blue-900/5'
                      : 'border-gray-200/90 bg-gray-50/40'
                  }`}
                >
                  <div className="flex gap-3">
                    <input
                      id="bulk-meta-memo"
                      type="checkbox"
                      className="mt-0.5 h-4 w-4 shrink-0 rounded border-gray-300 text-blue-600 focus:ring-blue-500 focus:ring-offset-0"
                      checked={bulkMetaApplyMemo}
                      onChange={(e) => setBulkMetaApplyMemo(e.target.checked)}
                      disabled={bulkMetadataModalBusy}
                    />
                    <div className="min-w-0 flex-1 space-y-2">
                      <div className="flex flex-wrap items-baseline justify-between gap-x-2 gap-y-0.5">
                        <label
                          htmlFor="bulk-meta-memo"
                          className="text-sm font-semibold text-gray-900 cursor-pointer"
                        >
                          Memo
                        </label>
                        {bulkMetaApplyMemo && (
                          <span className="text-[10px] font-semibold uppercase tracking-wider text-blue-600/90">
                            Included
                          </span>
                        )}
                      </div>
                      <p className="text-xs text-gray-500 leading-relaxed">
                        Optional note on the transaction. Leave empty to clear the memo everywhere.
                      </p>
                      <textarea
                        className="input w-full min-h-[80px] text-sm resize-y"
                        value={bulkMetaMemo}
                        onChange={(e) => setBulkMetaMemo(e.target.value)}
                        placeholder="Memo (optional)"
                        disabled={!bulkMetaApplyMemo || bulkMetadataModalBusy}
                        rows={3}
                      />
                    </div>
                  </div>
                </div>

                <div className="rounded-xl border border-amber-200/90 bg-amber-50/40 p-4 space-y-3">
                  <div className="flex items-start gap-2.5">
                    <ArrowsRightLeftIcon
                      className="w-5 h-5 shrink-0 text-amber-700 mt-0.5"
                      aria-hidden
                    />
                    <div className="min-w-0 flex-1 space-y-1">
                      <h4 className="text-sm font-semibold text-gray-900">Allocate shared costs</h4>
                      <p className="text-xs text-gray-600 leading-relaxed">
                        Spreads shared-cost lines (tax, shipping, discount, tip, fees, personal
                        shopper, etc.) across categories (same as the line items control on each
                        document). Only runs for selections that are still
                        pending review and have unallocated shared-cost
                        lines; others are skipped.
                      </p>
                    </div>
                  </div>
                  <button
                    type="button"
                    className="btn btn-secondary w-full text-sm font-semibold"
                    onClick={() => void handleBulkAllocateSharedCosts()}
                    disabled={bulkMetaQboLoading || bulkMetaSaving || isStartingBulkAllocate}
                  >
                    {isStartingBulkAllocate ? (
                      <span className="inline-flex items-center justify-center gap-2">
                        <ArrowPathIcon className="h-4 w-4 animate-spin" aria-hidden />
                        Allocating…
                      </span>
                    ) : (
                      'Allocate for eligible selected documents'
                    )}
                  </button>
                </div>

                <p className="flex items-start gap-2 rounded-lg bg-gray-50 px-3 py-2.5 text-xs text-gray-600 leading-relaxed">
                  <QuestionMarkCircleIcon className="w-4 h-4 shrink-0 text-gray-400 mt-0.5" aria-hidden />
                  <span>
                    Only checked sections are sent to the server. Combine multiple fields in one
                    apply if you need to fix several things at once.
                  </span>
                </p>
              </div>
            </div>

            <div className="flex-shrink-0 flex flex-col-reverse gap-2 border-t border-gray-100 bg-gradient-to-b from-gray-50/30 to-gray-50 px-5 py-4 sm:flex-row sm:justify-end sm:gap-3">
              <button
                type="button"
                className="btn btn-secondary w-full sm:w-auto sm:min-w-[5.5rem]"
                disabled={bulkMetadataModalBusy}
                onClick={() => setBulkMetadataOpen(false)}
              >
                Cancel
              </button>
              <button
                type="button"
                className="btn btn-primary w-full sm:w-auto min-h-[2.5rem] px-5 font-semibold shadow-sm disabled:shadow-none"
                disabled={bulkMetadataModalBusy || bulkMetaQboLoading}
                onClick={() => void handleBulkMetadataSubmit()}
              >
                {bulkMetaSaving ? (
                  <span className="inline-flex items-center justify-center gap-2">
                    <ArrowPathIcon className="h-4 w-4 animate-spin" aria-hidden />
                    Applying…
                  </span>
                ) : (
                  'Apply changes'
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Upload / re-run: choose optional extraction hints (email rule) */}
      {processingHintsModal && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/40 backdrop-blur-[2px] p-4"
          role="dialog"
          aria-modal="true"
          aria-labelledby="processing-hints-title"
        >
          <div className="bg-white rounded-2xl overflow-hidden shadow-2xl ring-1 ring-black/5 max-w-xl w-full max-h-[92vh] sm:max-h-[min(85vh,720px)] flex flex-col">
            <div className="flex-shrink-0 px-5 pt-5 pb-4 border-b border-gray-100">
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <h3
                    id="processing-hints-title"
                    className="text-lg font-semibold text-gray-900 tracking-tight"
                  >
                    {processingHintsModal.kind === 'upload' ? 'Upload files' : 'Re-run extraction'}
                  </h3>
                  <p className="mt-1 text-sm text-gray-500 leading-snug">
                    {processingHintsModal.kind === 'upload'
                      ? 'Optional: pick an email rule to apply its extraction hints to this batch (configured on Email Rules). CSV uploads use the same GPT hints but not the PDF-only Vision steps.'
                      : 'Optional: pick an email rule to force its hints for every selected document. Auto uses vendor-matched hints when available.'}
                  </p>
                </div>
                <button
                  type="button"
                  onClick={() => !processingHintsModalBusy && closeProcessingHintsModal()}
                  className="flex-shrink-0 rounded-lg p-1.5 text-gray-400 transition-colors hover:bg-gray-100 hover:text-gray-600 disabled:pointer-events-none disabled:opacity-40"
                  disabled={processingHintsModalBusy}
                  aria-label="Close"
                >
                  <XMarkIcon className="w-5 h-5" />
                </button>
              </div>
            </div>

            <div className="relative flex-1 min-h-0 overflow-y-auto px-5 py-4">
              {emailRulesLoading && (
                <div className="absolute inset-0 z-10 flex flex-col items-center justify-center gap-3 bg-white/85 backdrop-blur-sm">
                  <ArrowPathIcon className="h-8 w-8 text-blue-600 animate-spin" aria-hidden />
                  <p className="text-sm font-medium text-gray-700">Loading email rules</p>
                </div>
              )}

              <div className="space-y-4">
                {processingHintsModal.kind === 'upload' && (
                  <div>
                    <p className="text-xs font-medium text-gray-600 mb-2">
                      {processingHintsModal.files.length} file
                      {processingHintsModal.files.length === 1 ? '' : 's'}
                    </p>
                    <ul className="max-h-36 overflow-y-auto rounded-lg border border-gray-200 bg-gray-50/80 px-3 py-2 text-sm text-gray-800 space-y-1">
                      {processingHintsModal.files.map((f) => (
                        <li key={`${f.name}-${f.size}-${f.lastModified}`} className="truncate" title={f.name}>
                          {f.name}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}

                {processingHintsModal.kind === 'reprocess' && (
                  <p className="text-sm text-gray-700">
                    <span className="font-semibold tabular-nums">{selectedIds.size}</span> document
                    {selectedIds.size === 1 ? '' : 's'} selected. Extraction runs in the background; you can
                    keep working elsewhere.
                  </p>
                )}

                {processingHintsModal.kind === 'reprocess' && (
                  <p className="rounded-lg bg-amber-50 px-3 py-2.5 text-xs text-amber-900 leading-relaxed border border-amber-100">
                    Re-running overwrites line items, totals, and other extracted fields from the stored
                    document (PDF, Drive file, or saved text). This cannot be undone from here.
                  </p>
                )}

                <div className="space-y-2">
                  <label className="text-sm font-semibold text-gray-900">Parsing hints</label>
                  <SearchableSelect
                    options={processingHintsRuleOptions}
                    value={processingHintsRuleId}
                    onChange={(id) => setProcessingHintsRuleId(id ?? '')}
                    placeholder="Auto"
                    disabled={emailRulesLoading}
                    className="w-full"
                    useViewportForPlacement
                    preferBelow
                  />
                  <p className="text-xs text-gray-500 leading-relaxed">
                    Auto leaves uploads without a rule hint and matches hints by document vendor when you re-run.
                  </p>
                </div>
              </div>
            </div>

            <div className="flex-shrink-0 flex flex-col-reverse gap-2 border-t border-gray-100 bg-gradient-to-b from-gray-50/30 to-gray-50 px-5 py-4 sm:flex-row sm:justify-end sm:gap-3">
              <button
                type="button"
                className="btn btn-secondary w-full sm:w-auto sm:min-w-[5.5rem]"
                disabled={processingHintsModalBusy}
                onClick={() => closeProcessingHintsModal()}
              >
                Cancel
              </button>
              <button
                type="button"
                className="btn btn-primary w-full sm:w-auto min-h-[2.5rem] px-5 font-semibold shadow-sm disabled:shadow-none"
                disabled={processingHintsModalBusy}
                onClick={() => void confirmProcessingHintsModal()}
              >
                {processingHintsModalBusy ? (
                  <span className="inline-flex items-center justify-center gap-2">
                    <ArrowPathIcon className="h-4 w-4 animate-spin" aria-hidden />
                    {processingHintsModal.kind === 'upload' ? 'Uploading…' : 'Starting…'}
                  </span>
                ) : processingHintsModal.kind === 'upload' ? (
                  `Upload ${processingHintsModal.files.length} file${processingHintsModal.files.length === 1 ? '' : 's'}`
                ) : (
                  'Re-run extraction'
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Duplicate check result modal */}
      {duplicateCheckResult && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-2 sm:p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-lg w-full max-h-[95vh] sm:max-h-[80vh] flex flex-col">
            <div className="flex items-center justify-between p-3 sm:p-4 border-b">
              <h3 className="text-base sm:text-lg font-semibold text-gray-900">
                QBO Duplicate Check
              </h3>
              <button
                onClick={() => setDuplicateCheckResult(null)}
                className="text-gray-400 hover:text-gray-500 p-1"
              >
                <XMarkIcon className="w-5 h-5" />
              </button>
            </div>
            <div className="p-3 sm:p-4 overflow-y-auto flex-1">
              <div className="mb-4 p-3 bg-amber-50 border border-amber-200 rounded-lg">
                <p className="text-amber-700 font-medium text-sm sm:text-base">
                  Found {duplicateCheckResult.duplicate_count} duplicate(s) in QBO
                </p>
                <p className="text-amber-600 text-xs sm:text-sm mt-1">
                  Checked {duplicateCheckResult.checked_count} pending doc(s)
                </p>
                <p className="text-amber-700 text-xs sm:text-sm mt-2">
                  The &quot;Duplicate in QBO&quot; flag is saved on these documents. Use the Flags filter above the
                  table to find them later.
                </p>
              </div>
              <div className="space-y-2">
                {duplicateCheckResult.duplicates.map((dup) => (
                  <div key={dup.id} className="bg-white p-2 sm:p-3 rounded border border-amber-100">
                    <div className="flex flex-col sm:flex-row sm:justify-between sm:items-start gap-1 sm:gap-2">
                      <div className="min-w-0">
                        <p className="font-medium text-gray-800 text-sm truncate">
                          {dup.vendor_name}
                        </p>
                        <p className="text-gray-600 text-xs sm:text-sm">
                          Ref: {dup.reference_number}
                        </p>
                      </div>
                      <a
                        href={qboTxnUrl(dup.qbo_entity_type, dup.qbo_id)}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-blue-600 hover:text-blue-800 text-xs sm:text-sm underline flex-shrink-0"
                      >
                        View in QBO
                      </a>
                    </div>
                    <p className="text-amber-600 text-xs mt-1">
                      Matches {dup.qbo_entity_type} #{dup.qbo_doc_number}
                    </p>
                  </div>
                ))}
              </div>
            </div>
            <div className="p-3 sm:p-4 border-t bg-gray-50">
              <button
                onClick={() => setDuplicateCheckResult(null)}
                className="btn btn-primary w-full"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Reprocess result modal */}
      {reprocessResult && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-2 sm:p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-lg w-full max-h-[95vh] sm:max-h-[80vh] flex flex-col">
            <div className="flex items-center justify-between p-3 sm:p-4 border-b">
              <h3 className="text-base sm:text-lg font-semibold text-gray-900">
                Reprocess Results
              </h3>
              <button
                onClick={() => setReprocessResult(null)}
                className="text-gray-400 hover:text-gray-500 p-1"
              >
                <XMarkIcon className="w-5 h-5" />
              </button>
            </div>
            <div className="p-3 sm:p-4 overflow-y-auto flex-1">
              {reprocessResult.reprocessed_count > 0 && (
                <div className="mb-4 p-3 bg-green-50 border border-green-200 rounded-lg">
                  <p className="text-green-700 font-medium text-sm sm:text-base">
                    {reprocessResult.reprocessed_count} reprocessed
                  </p>
                </div>
              )}
              {reprocessResult.failed_count > 0 && (
                <div className="p-3 bg-red-50 border border-red-200 rounded-lg">
                  <p className="text-red-700 font-medium mb-3 text-sm sm:text-base">
                    {reprocessResult.failed_count} failed
                  </p>
                  <div className="space-y-2">
                    {reprocessResult.failed_docs.map((doc) => (
                      <div key={doc.id} className="bg-white p-2 rounded border border-red-100">
                        <p className="font-medium text-gray-800 text-sm truncate">
                          Ref: {doc.reference_number || `#${doc.id}`}
                        </p>
                        <p className="text-red-600 text-xs sm:text-sm mt-1">
                          {doc.error}
                        </p>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
            <div className="p-3 sm:p-4 border-t bg-gray-50">
              <button
                onClick={() => setReprocessResult(null)}
                className="btn btn-primary w-full"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
